import type { Riddle } from '@hollowveil/shared';

/**
 * Riddles et Dialogues pour "Le (R)éveil de l'Éducatrice"
 *
 * Ce fichier contient tous les dialogues interactifs du jeu.
 * Les riddles sont des moments de réflexion ou de choix pour le joueur.
 */

export const reveilRiddles: Riddle[] = [
  // ==========================================
  // SANCTUAIRE - Introduction avec l'Oracle
  // ==========================================
  {
    id: 'oracle_welcome',
    title: "Bienvenue, Voyageuse",
    description: "L'Oracle Chevelu t'accueille dans le Sanctuaire des Dimensions.",
    type: 'dialogue',
    world: 'real',
    dialogueSequence: [
      {
        speaker: "L'Oracle Chevelu",
        text: "*ajuste sa magnifique perruque* Ah ! Te voilà enfin, Côtelette !",
        emotion: 'mysterious',
      },
      {
        speaker: "L'Oracle Chevelu",
        text: "Je t'attendais. Les dimensions ont besoin de quelqu'un comme toi.",
        emotion: 'wise',
      },
      {
        speaker: "L'Oracle Chevelu",
        text: "Tu as un don rare : voir au-delà des apparences, comprendre les coeurs.",
        emotion: 'encouraging',
      },
      {
        speaker: "L'Oracle Chevelu",
        text: "Des âmes en détresse attendent dans 5 mondes différents. Seule toi peux les aider.",
        emotion: 'serious',
      },
      {
        speaker: "L'Oracle Chevelu",
        text: "Mais d'abord... apprends à voir le monde SPECTRAL. Appuie sur ESPACE ou SWIPE vers le haut !",
        emotion: 'teaching',
      },
    ],
    choices: [
      {
        text: "Je suis prête à aider !",
        result: 'positive',
        nextRiddleId: 'tutorial_spectral',
      },
      {
        text: "Pourquoi moi ?",
        result: 'curious',
        nextRiddleId: 'oracle_why_me',
      },
    ],
  },
  {
    id: 'oracle_why_me',
    title: "Pourquoi toi ?",
    description: "L'Oracle explique pourquoi Côtelette a été choisie.",
    type: 'dialogue',
    world: 'real',
    dialogueSequence: [
      {
        speaker: "L'Oracle Chevelu",
        text: "*sourire sous la perruque* Excellente question !",
        emotion: 'amused',
      },
      {
        speaker: "L'Oracle Chevelu",
        text: "Parce que tu es une Éducatrice Spécialisée. Tu comprends que chaque personne est unique.",
        emotion: 'wise',
      },
      {
        speaker: "L'Oracle Chevelu",
        text: "Tu sais voir les forces là où d'autres ne voient que des faiblesses.",
        emotion: 'proud',
      },
      {
        speaker: "L'Oracle Chevelu",
        text: "Et surtout... quelqu'un qui t'aime beaucoup a demandé mon aide pour te montrer à quel point tu es extraordinaire.",
        emotion: 'mysterious',
      },
    ],
    choices: [
      {
        text: "Quelqu'un qui m'aime... ?",
        result: 'curious',
        nextRiddleId: 'tutorial_spectral',
      },
    ],
  },
  {
    id: 'tutorial_spectral',
    title: "Le Voile entre les Mondes",
    description: "Apprends à passer entre le monde réel et le monde spectral.",
    type: 'action',
    world: 'both',
    instruction: "Appuie sur ESPACE ou SWIPE vers le haut pour voir le monde SPECTRAL",
    successCondition: {
      type: 'worldChange',
      targetWorld: 'spectral',
    },
    successMessage: "Parfait ! Tu vois maintenant le monde spectral - les émotions cachées des êtres.",
    failureMessage: "Essaie encore. Le monde spectral révèle ce que les yeux normaux ne peuvent voir.",
  },

  // ==========================================
  // WONDERLAND - Le Chapelier Fou (TDAH)
  // ==========================================
  {
    id: 'hatter_first_meeting',
    title: "Un thé... interrompu",
    description: "Le Chapelier Fou semble avoir du mal à se concentrer.",
    type: 'dialogue',
    world: 'real',
    dialogueSequence: [
      {
        speaker: "Le Chapelier Fou",
        text: "Un thé ! Oui un thé ! Non attends... un gâteau ? Où est ma tasse ? BONJOUR !",
        emotion: 'chaotic',
      },
      {
        speaker: "Le Chapelier Fou",
        text: "Oh une visiteuse ! Assieds-toi ! Non pas là ! Là ! Non... *soupir*",
        emotion: 'frustrated',
      },
      {
        speaker: "Le Chapelier Fou",
        text: "Pardon... tout le monde dit que je suis fou. Peut-être qu'ils ont raison.",
        emotion: 'sad',
      },
    ],
    choices: [
      {
        text: "Tu n'es pas fou, tu penses juste différemment.",
        result: 'empathetic',
        nextRiddleId: 'hatter_understanding',
      },
      {
        text: "Qu'est-ce qui te préoccupe vraiment ?",
        result: 'curious',
        nextRiddleId: 'hatter_problem',
      },
    ],
  },
  {
    id: 'hatter_understanding',
    title: "Comprendre le Chapelier",
    description: "Le Chapelier commence à s'ouvrir.",
    type: 'dialogue',
    world: 'real',
    dialogueSequence: [
      {
        speaker: "Le Chapelier Fou",
        text: "*s'arrête net* Tu... tu ne me trouves pas fou ?",
        emotion: 'surprised',
      },
      {
        speaker: "Le Chapelier Fou",
        text: "Mon cerveau est comme... mille papillons. Ils volent tous en même temps.",
        emotion: 'explaining',
      },
      {
        speaker: "Le Chapelier Fou",
        text: "Je veux faire des chapeaux magnifiques, mais je n'arrive jamais à en finir un.",
        emotion: 'frustrated',
      },
    ],
    hint: "Passe en mode SPECTRAL pour voir ce qui se passe vraiment dans sa tête.",
    choices: [
      {
        text: "Je vais t'aider à trouver un système qui fonctionne pour TOI.",
        result: 'supportive',
        nextRiddleId: 'hatter_solution',
      },
    ],
  },
  {
    id: 'hatter_problem',
    title: "Le vrai problème",
    description: "Le Chapelier révèle ce qui le tourmente.",
    type: 'observation',
    world: 'spectral',
    instruction: "Observe le Chapelier en mode SPECTRAL pour voir son tourment intérieur.",
    hint: "En mode spectral, tu verras les pensées qui tourbillonnent autour de lui.",
    successMessage: "Tu vois maintenant : des dizaines de pensées, projets et idées tournoient autour de lui sans repos.",
  },
  {
    id: 'hatter_solution',
    title: "Un chapeau à la fois",
    description: "Aide le Chapelier à trouver sa méthode.",
    type: 'puzzle',
    world: 'real',
    instruction: "Aide le Chapelier à organiser ses tâches : trouve les 3 tasses de thé spéciales dans la pièce.",
    hint: "Chaque tasse représente une étape : COMMENCER, CONTINUER, FINIR",
    items: ['tasse_commencer', 'tasse_continuer', 'tasse_finir'],
    successMessage: "Le Chapelier comprend maintenant ! Un projet à la fois, avec des étapes claires.",
    reward: {
      type: 'item',
      itemId: 'chapeau_wonderland',
      description: "Chapeau du Chapelier - Un rappel que chaque grande création commence par un petit pas.",
    },
  },
  {
    id: 'hatter_resolved',
    title: "Le Chapelier apaisé",
    description: "Le Chapelier a trouvé sa paix intérieure.",
    type: 'dialogue',
    world: 'real',
    dialogueSequence: [
      {
        speaker: "Le Chapelier Fou",
        text: "*travaille calmement* Regarde ! J'ai fini un chapeau. UN chapeau entier !",
        emotion: 'proud',
      },
      {
        speaker: "Le Chapelier Fou",
        text: "Tu m'as appris quelque chose d'important : mon cerveau n'est pas mon ennemi.",
        emotion: 'grateful',
      },
      {
        speaker: "Le Chapelier Fou",
        text: "Il fonctionne juste... différemment. Et c'est OK.",
        emotion: 'peaceful',
      },
      {
        speaker: "Le Chapelier Fou",
        text: "Prends ce chapeau. Tu l'as mérité, mon amie.",
        emotion: 'giving',
      },
    ],
    choices: [
      {
        text: "Merci, Chapelier. Continue de créer !",
        result: 'complete',
        unlocks: 'portal_hawkins',
      },
    ],
  },

  // Chat du Cheshire - Énigme pour le 3ème souvenir
  {
    id: 'cheshire_riddle',
    title: "L'Énigme du Chat",
    description: "Le Chat du Cheshire te pose une énigme en échange du 3ème souvenir.",
    type: 'dialogue',
    world: 'both',
    dialogueSequence: [
      {
        speaker: "Chat du Cheshire",
        text: "*apparaît progressivement, sourire d'abord* Tiens, tiens... une visiteuse curieuse !",
        emotion: 'mysterious',
      },
      {
        speaker: "Chat du Cheshire",
        text: "Tu cherches un souvenir, n'est-ce pas ? J'en ai un... mais tout a un prix.",
        emotion: 'playful',
      },
      {
        speaker: "Chat du Cheshire",
        text: "Réponds à mon énigme, et le souvenir sera tien.",
        emotion: 'challenging',
      },
      {
        speaker: "Chat du Cheshire",
        text: "Je suis partout et nulle part, je peux faire rire ou pleurer...",
        emotion: 'mysterious',
      },
      {
        speaker: "Chat du Cheshire",
        text: "On me cherche quand on l'a perdu, et on me fuit quand on en a trop. Qui suis-je ?",
        emotion: 'questioning',
      },
    ],
    choices: [
      {
        text: "Le temps !",
        result: 'correct',
        nextRiddleId: 'cheshire_reward',
      },
      {
        text: "L'attention !",
        result: 'correct',
        nextRiddleId: 'cheshire_reward',
      },
      {
        text: "Le regard des autres !",
        result: 'correct',
        nextRiddleId: 'cheshire_reward',
      },
      {
        text: "Je ne sais pas...",
        result: 'hint',
        nextRiddleId: 'cheshire_hint',
      },
    ],
  },
  {
    id: 'cheshire_hint',
    title: "Un indice du Chat",
    description: "Le Chat te donne un indice.",
    type: 'dialogue',
    world: 'both',
    dialogueSequence: [
      {
        speaker: "Chat du Cheshire",
        text: "*son sourire s'élargit* Un indice ? Mais bien sûr...",
        emotion: 'amused',
      },
      {
        speaker: "Chat du Cheshire",
        text: "Pense au Chapelier. Qu'est-ce qui le tourmente ?",
        emotion: 'helpful',
      },
      {
        speaker: "Chat du Cheshire",
        text: "Ce n'est pas le thé qui refroidit... c'est ce qui passe pendant qu'il hésite.",
        emotion: 'cryptic',
      },
    ],
    choices: [
      {
        text: "Le temps !",
        result: 'correct',
        nextRiddleId: 'cheshire_reward',
      },
      {
        text: "L'attention des autres !",
        result: 'correct',
        nextRiddleId: 'cheshire_reward',
      },
    ],
  },
  {
    id: 'cheshire_reward',
    title: "Le Souvenir du Chat",
    description: "Le Chat te donne le 3ème souvenir.",
    type: 'dialogue',
    world: 'both',
    dialogueSequence: [
      {
        speaker: "Chat du Cheshire",
        text: "*ronronne* Excellent ! Tu comprends les choses importantes.",
        emotion: 'pleased',
      },
      {
        speaker: "Chat du Cheshire",
        text: "Voici le souvenir. C'était la meilleure tea party... parce que personne ne regardait l'heure.",
        emotion: 'nostalgic',
      },
      {
        speaker: "Chat du Cheshire",
        text: "*tend une photo* Le Chapelier riait tellement ce jour-là...",
        emotion: 'gentle',
      },
      {
        speaker: "Chat du Cheshire",
        text: "*commence à disparaître* Au revoir... ou plutôt, à la prochaine fois que tu me chercheras !",
        emotion: 'playful',
      },
    ],
    choices: [
      {
        text: "Merci, Chat du Cheshire !",
        result: 'complete',
        reward: {
          type: 'item',
          itemId: 'memory_photo_3',
          description: "Souvenir Heureux #3 - Une photo de la meilleure tea party, où le temps n'existait pas.",
        },
      },
    ],
  },

  // ==========================================
  // HAWKINS - Vékeuna / Eleven (Identité)
  // ==========================================
  {
    id: 'eleven_first_meeting',
    title: "La fille aux pouvoirs",
    description: "Vékeuna se cache dans le sous-sol.",
    type: 'dialogue',
    world: 'real',
    dialogueSequence: [
      {
        speaker: "Vékeuna",
        text: "*recule* Qui es-tu ? Tu travailles pour le laboratoire ?",
        emotion: 'fearful',
      },
      {
        speaker: "Vékeuna",
        text: "Je... je suis dangereuse. Tu devrais partir.",
        emotion: 'warning',
      },
      {
        speaker: "Vékeuna",
        text: "*fait léviter un objet involontairement* Tu vois ? Je ne contrôle rien.",
        emotion: 'distressed',
      },
    ],
    choices: [
      {
        text: "Tes pouvoirs ne te définissent pas. Tu es plus que ça.",
        result: 'empathetic',
        nextRiddleId: 'eleven_identity',
      },
      {
        text: "Qu'est-ce qu'ils t'ont fait au laboratoire ?",
        result: 'curious',
        nextRiddleId: 'eleven_past',
      },
    ],
  },
  {
    id: 'eleven_identity',
    title: "Plus qu'un numéro",
    description: "Aide Vékeuna à comprendre qu'elle est plus que ses pouvoirs.",
    type: 'observation',
    world: 'spectral',
    instruction: "En mode SPECTRAL, trouve les souvenirs positifs de Vékeuna cachés dans la pièce.",
    hint: "Cherche les orbes de lumière - ce sont ses bons souvenirs.",
    successMessage: "Tu as trouvé ses souvenirs : des moments avec ses amis, des rires, de l'amour.",
  },
  {
    id: 'eleven_past',
    title: "Le passé de Vékeuna",
    description: "Vékeuna se confie sur son passé.",
    type: 'dialogue',
    world: 'real',
    dialogueSequence: [
      {
        speaker: "Vékeuna",
        text: "Ils m'ont... créée. Je n'étais qu'un numéro : Onze.",
        emotion: 'painful',
      },
      {
        speaker: "Vékeuna",
        text: "J'ai fait des choses terribles. Avec mes pouvoirs.",
        emotion: 'guilty',
      },
      {
        speaker: "Vékeuna",
        text: "Comment puis-je être quelqu'un de bien... après tout ça ?",
        emotion: 'desperate',
      },
    ],
    choices: [
      {
        text: "Ce qui compte, c'est ce que tu CHOISIS d'être maintenant.",
        result: 'supportive',
        nextRiddleId: 'eleven_choice',
      },
    ],
  },
  {
    id: 'eleven_choice',
    title: "Le choix de Vékeuna",
    description: "Vékeuna doit faire un choix important.",
    type: 'puzzle',
    world: 'both',
    instruction: "Aide Vékeuna à connecter ses pouvoirs à quelque chose de positif. Trouve le Demodog blessé en mode spectral.",
    hint: "En mode spectral, une créature blessée appelle à l'aide.",
    successMessage: "Vékeuna utilise ses pouvoirs pour soigner le Demodog. Elle comprend qu'elle peut choisir comment utiliser ses dons.",
    reward: {
      type: 'item',
      itemId: 'bracelet_hawkins',
      description: "Bracelet de l'Amitié - Un symbole que l'identité se construit avec ceux qu'on aime.",
    },
  },
  {
    id: 'eleven_resolved',
    title: "Friends don't lie",
    description: "Vékeuna a trouvé sa vraie identité.",
    type: 'dialogue',
    world: 'real',
    dialogueSequence: [
      {
        speaker: "Vékeuna",
        text: "*sourit* Je comprends maintenant. Je ne suis pas mes pouvoirs.",
        emotion: 'peaceful',
      },
      {
        speaker: "Vékeuna",
        text: "Je suis Vékeuna. L'amie de Mike, Dustin, Lucas. Et maintenant ton amie aussi.",
        emotion: 'happy',
      },
      {
        speaker: "Vékeuna",
        text: "Friends don't lie. Merci de ne pas avoir menti sur qui je pouvais être.",
        emotion: 'grateful',
      },
    ],
    choices: [
      {
        text: "Tu es extraordinaire, Vékeuna. Ne l'oublie jamais.",
        result: 'complete',
        unlocks: 'portal_jurassic',
      },
    ],
  },

  // ==========================================
  // JURASSIC PARK - Blue (Confiance)
  // ==========================================
  {
    id: 'blue_first_meeting',
    title: "La reine des Raptors",
    description: "Blue est méfiante envers les humains.",
    type: 'dialogue',
    world: 'real',
    dialogueSequence: [
      {
        speaker: "Narrateur",
        text: "Un raptor bleu te fixe intensément. C'est Blue, la plus intelligente de sa meute.",
        emotion: 'tense',
      },
      {
        speaker: "Blue",
        text: "*grognement méfiant* (Elle ne te fait pas confiance. Les humains l'ont trahie avant.)",
        emotion: 'suspicious',
      },
    ],
    hint: "Ne fais pas de mouvements brusques. La confiance se gagne.",
    choices: [
      {
        text: "*S'accroupir lentement et tendre la main*",
        result: 'patient',
        nextRiddleId: 'blue_trust_building',
      },
      {
        text: "*Rester immobile et la laisser s'approcher*",
        result: 'respectful',
        nextRiddleId: 'blue_trust_building',
      },
    ],
  },
  {
    id: 'blue_trust_building',
    title: "Construire la confiance",
    description: "La confiance se gagne pas à pas.",
    type: 'puzzle',
    world: 'real',
    instruction: "Trouve de la nourriture pour Blue sans faire de mouvements brusques. Cherche près des buissons.",
    hint: "Blue observe chacun de tes gestes. Sois patient(e).",
    successMessage: "Blue accepte la nourriture. C'est un premier pas.",
  },
  {
    id: 'blue_baby_raptor',
    title: "Le bébé raptor",
    description: "Un bébé raptor est en danger.",
    type: 'observation',
    world: 'spectral',
    instruction: "En mode SPECTRAL, tu sens la détresse de Blue. Quelque chose ne va pas.",
    hint: "Suis le regard de Blue - elle surveille quelque chose dans les hautes herbes.",
    successMessage: "Tu découvres un bébé raptor coincé ! Blue a besoin de ton aide.",
  },
  {
    id: 'blue_rescue',
    title: "Sauvetage",
    description: "Sauve le bébé raptor avec l'aide de Blue.",
    type: 'puzzle',
    world: 'both',
    instruction: "Travaille avec Blue pour libérer le bébé raptor. Elle te guide, fais-lui confiance.",
    hint: "Blue te montre où appuyer. Elle te fait enfin confiance.",
    successMessage: "Le bébé raptor est libre ! Blue te regarde avec reconnaissance.",
    reward: {
      type: 'companion',
      itemId: 'baby_raptor',
      description: "Bébé Raptor - Il te suivra partout. Tu as gagné sa confiance et celle de Blue.",
    },
  },
  {
    id: 'blue_resolved',
    title: "La meute",
    description: "Tu fais maintenant partie de la famille.",
    type: 'dialogue',
    world: 'real',
    dialogueSequence: [
      {
        speaker: "Narrateur",
        text: "Blue pose sa tête contre ta main. Un geste de confiance absolue.",
        emotion: 'touching',
      },
      {
        speaker: "Blue",
        text: "*son doux* (Tu fais partie de sa meute maintenant. Le bébé raptor te suivra.)",
        emotion: 'accepting',
      },
      {
        speaker: "Narrateur",
        text: "Tu as prouvé que la confiance, même brisée, peut être reconstruite.",
        emotion: 'hopeful',
      },
    ],
    choices: [
      {
        text: "*Caresser Blue une dernière fois avant de partir*",
        result: 'complete',
        unlocks: 'portal_winterfell',
      },
    ],
  },

  // ==========================================
  // WINTERFELL - Tyrion (Syndrome de l'imposteur)
  // ==========================================
  {
    id: 'tyrion_first_meeting',
    title: "Le Nain de Castral Roc",
    description: "Tyrion noie ses doutes dans le vin.",
    type: 'dialogue',
    world: 'real',
    dialogueSequence: [
      {
        speaker: "Tyrion Lannister",
        text: "*lève sa coupe* Ah, une visiteuse ! Viens donc partager un verre avec le monstre de la famille.",
        emotion: 'sarcastic',
      },
      {
        speaker: "Tyrion Lannister",
        text: "On dit que je suis intelligent. *rire amer* Si seulement c'était suffisant.",
        emotion: 'bitter',
      },
      {
        speaker: "Tyrion Lannister",
        text: "Mon père me méprise. Ma soeur veut ma mort. Et le monde ne voit qu'un nain.",
        emotion: 'hurt',
      },
    ],
    choices: [
      {
        text: "Ta taille ne définit pas ta valeur.",
        result: 'empathetic',
        nextRiddleId: 'tyrion_worth',
      },
      {
        text: "Tu es la Main du Roi. Tu as prouvé ta valeur.",
        result: 'logical',
        nextRiddleId: 'tyrion_doubt',
      },
    ],
  },
  {
    id: 'tyrion_worth',
    title: "La vraie valeur",
    description: "Aide Tyrion à voir au-delà des apparences.",
    type: 'dialogue',
    world: 'real',
    dialogueSequence: [
      {
        speaker: "Tyrion Lannister",
        text: "*pause* Tu es bien la première à me dire ça sans ironie.",
        emotion: 'surprised',
      },
      {
        speaker: "Tyrion Lannister",
        text: "Mais quand tout le monde te dit que tu n'es rien... comment ne pas finir par le croire ?",
        emotion: 'vulnerable',
      },
    ],
    hint: "En mode SPECTRAL, tu peux voir les doutes qui l'enchaînent.",
    choices: [
      {
        text: "Montre-moi ce que tu vois quand tu te regardes.",
        result: 'understanding',
        nextRiddleId: 'tyrion_mirror',
      },
    ],
  },
  {
    id: 'tyrion_doubt',
    title: "Les doutes de Tyrion",
    description: "Tyrion révèle son syndrome de l'imposteur.",
    type: 'dialogue',
    world: 'real',
    dialogueSequence: [
      {
        speaker: "Tyrion Lannister",
        text: "Main du Roi ? *rire nerveux* J'attends chaque jour qu'on découvre que je suis un imposteur.",
        emotion: 'anxious',
      },
      {
        speaker: "Tyrion Lannister",
        text: "Chaque succès me semble être de la chance. Chaque échec confirme que je ne mérite pas ma place.",
        emotion: 'confessing',
      },
    ],
    choices: [
      {
        text: "C'est le syndrome de l'imposteur. Beaucoup de gens brillants en souffrent.",
        result: 'informative',
        nextRiddleId: 'tyrion_mirror',
      },
    ],
  },
  {
    id: 'tyrion_mirror',
    title: "Le Miroir de Vérité",
    description: "Montre à Tyrion ce que les autres voient vraiment.",
    type: 'observation',
    world: 'spectral',
    instruction: "En mode SPECTRAL, trouve le Miroir de Vérité dans la pièce. Il montre ce que les autres pensent vraiment de Tyrion.",
    hint: "Le miroir est caché derrière les tonneaux de vin.",
    successMessage: "Le miroir montre des dizaines de personnes : Varys, Jon, Daenerys... tous reconnaissent l'intelligence et la valeur de Tyrion.",
  },
  {
    id: 'tyrion_resolved',
    title: "Je bois et je sais des choses",
    description: "Tyrion accepte enfin sa propre valeur.",
    type: 'dialogue',
    world: 'real',
    dialogueSequence: [
      {
        speaker: "Tyrion Lannister",
        text: "*pose sa coupe* Tu sais quoi ? Tu as raison.",
        emotion: 'determined',
      },
      {
        speaker: "Tyrion Lannister",
        text: "Je ne peux pas contrôler ce que les autres pensent de moi. Mais je peux choisir ce que JE pense de moi.",
        emotion: 'enlightened',
      },
      {
        speaker: "Tyrion Lannister",
        text: "Je bois et je sais des choses. Et c'est suffisant.",
        emotion: 'confident',
      },
      {
        speaker: "Tyrion Lannister",
        text: "Prends cette broche. Elle appartenait à une Main du Roi qui méritait son titre. Tout comme moi.",
        emotion: 'grateful',
      },
    ],
    choices: [
      {
        text: "Tu as toujours mérité ton titre, Tyrion.",
        result: 'complete',
        unlocks: 'portal_middleearth',
      },
    ],
    reward: {
      type: 'item',
      itemId: 'broche_main',
      description: "Broche de la Main du Roi - Un rappel que la vraie valeur vient de l'intérieur.",
    },
  },

  // ==========================================
  // TERRE DU MILIEU - Eärwen (Apprendre différemment)
  // ==========================================
  {
    id: 'earwen_first_meeting',
    title: "L'Elfe différente",
    description: "Eärwen est à l'écart des autres elfes.",
    type: 'dialogue',
    world: 'real',
    dialogueSequence: [
      {
        speaker: "Eärwen",
        text: "*ne lève pas les yeux de son parchemin* Encore quelqu'un pour me dire que je fais tout mal ?",
        emotion: 'defensive',
      },
      {
        speaker: "Eärwen",
        text: "Les autres elfes apprennent les chants en quelques jours. Moi, ça me prend des semaines.",
        emotion: 'frustrated',
      },
      {
        speaker: "Eärwen",
        text: "Je ne suis pas... normale. Pour une elfe.",
        emotion: 'sad',
      },
    ],
    choices: [
      {
        text: "Peut-être que tu apprends juste différemment ?",
        result: 'understanding',
        nextRiddleId: 'earwen_learning',
      },
      {
        text: "Montre-moi comment TU préfères apprendre.",
        result: 'curious',
        nextRiddleId: 'earwen_method',
      },
    ],
  },
  {
    id: 'earwen_learning',
    title: "Une autre façon",
    description: "Comprends comment Eärwen apprend.",
    type: 'dialogue',
    world: 'real',
    dialogueSequence: [
      {
        speaker: "Eärwen",
        text: "Différemment ? Mais il n'y a qu'UNE façon d'apprendre. Celle des Maîtres.",
        emotion: 'confused',
      },
      {
        speaker: "Eärwen",
        text: "Écouter, mémoriser, répéter. C'est comme ça depuis des millénaires.",
        emotion: 'resigned',
      },
      {
        speaker: "Eärwen",
        text: "Mais quand j'écoute... les mots s'envolent. Je ne retiens rien.",
        emotion: 'frustrated',
      },
    ],
    hint: "En mode SPECTRAL, observe comment son esprit traite vraiment l'information.",
    choices: [
      {
        text: "Et si on essayait autrement ?",
        result: 'supportive',
        nextRiddleId: 'earwen_experiment',
      },
    ],
  },
  {
    id: 'earwen_method',
    title: "Sa vraie méthode",
    description: "Découvre comment Eärwen apprend vraiment.",
    type: 'observation',
    world: 'spectral',
    instruction: "En mode SPECTRAL, observe Eärwen quand elle dessine. Tu verras comment son esprit fonctionne.",
    hint: "Elle dessine constamment sur ses parchemins...",
    successMessage: "Tu comprends ! Son esprit pense en IMAGES. Elle a besoin de visualiser pour apprendre.",
  },
  {
    id: 'earwen_experiment',
    title: "Une nouvelle approche",
    description: "Aide Eärwen à trouver SA méthode d'apprentissage.",
    type: 'puzzle',
    world: 'both',
    instruction: "Trouve les 3 outils qui pourraient aider Eärwen : les symboles elfiques illustrés, la harpe (pour apprendre en musique), et le livre d'images.",
    hint: "Cherche dans la bibliothèque et près de la fontaine.",
    items: ['symboles_illustres', 'harpe_apprentissage', 'livre_images'],
    successMessage: "Eärwen essaie les différentes méthodes. Son visage s'illumine quand elle utilise les illustrations !",
    reward: {
      type: 'item',
      itemId: 'plume_earwen',
      description: "Plume Elfique - Un rappel que chaque esprit a sa propre façon de briller.",
    },
  },
  {
    id: 'earwen_resolved',
    title: "Hannon le (Merci)",
    description: "Eärwen a trouvé sa voie.",
    type: 'dialogue',
    world: 'real',
    dialogueSequence: [
      {
        speaker: "Eärwen",
        text: "*chante un chant elfique complexe* Tu as entendu ? J'ai réussi !",
        emotion: 'joyful',
      },
      {
        speaker: "Eärwen",
        text: "Je l'ai appris en le dessinant. Chaque note est une couleur dans mon esprit.",
        emotion: 'excited',
      },
      {
        speaker: "Eärwen",
        text: "Tu m'as montré que je n'étais pas déficiente. J'apprends juste... différemment.",
        emotion: 'grateful',
      },
      {
        speaker: "Eärwen",
        text: "Hannon le, mellon. Merci, amie. Prends cette plume - que ta lumière guide d'autres âmes perdues.",
        emotion: 'giving',
      },
    ],
    choices: [
      {
        text: "Ta lumière brillait depuis le début, Eärwen. Il fallait juste la voir.",
        result: 'complete',
        unlocks: 'portal_finale',
      },
    ],
  },

  // ==========================================
  // FINALE - La Révélation de Benours
  // ==========================================
  {
    id: 'finale_convocation',
    title: "Convocation au Sanctuaire",
    description: "L'Oracle t'appelle pour une révélation importante.",
    type: 'dialogue',
    world: 'real',
    dialogueSequence: [
      {
        speaker: "L'Oracle Chevelu",
        text: "*voix mystérieuse* Tu as accompli l'impossible, Côtelette.",
        emotion: 'proud',
      },
      {
        speaker: "L'Oracle Chevelu",
        text: "5 mondes. 5 âmes sauvées. Tu as prouvé ce que je savais depuis le début.",
        emotion: 'knowing',
      },
      {
        speaker: "L'Oracle Chevelu",
        text: "Il est temps que tu saches la vérité. Sur moi. Sur tout cela.",
        emotion: 'serious',
      },
      {
        speaker: "L'Oracle Chevelu",
        text: "*ajuste nerveusement sa perruque* Euh... comment dire...",
        emotion: 'nervous',
      },
    ],
    choices: [
      {
        text: "Qui es-tu vraiment ?",
        result: 'curious',
        nextRiddleId: 'finale_revelation',
      },
    ],
  },
  {
    id: 'finale_revelation',
    title: "La Perruque Tombe",
    description: "Le moment de vérité.",
    type: 'dialogue',
    world: 'real',
    dialogueSequence: [
      {
        speaker: "L'Oracle Chevelu",
        text: "*la perruque glisse* Oh non... non non non...",
        emotion: 'panicked',
      },
      {
        speaker: "Narrateur",
        text: "La magnifique perruque tombe au sol, révélant... un crâne chauve et familier.",
        emotion: 'revelation',
      },
      {
        speaker: "Benours",
        text: "*sourire gêné* ... Surprise, mon amour ?",
        emotion: 'sheepish',
      },
      {
        speaker: "Benours",
        text: "C'est moi. Ton Benours. L'Oracle Chevelu, c'était moi depuis le début.",
        emotion: 'revealing',
      },
    ],
    choices: [
      {
        text: "BENOURS ?! Mais... pourquoi tout ça ?",
        result: 'shocked',
        nextRiddleId: 'finale_explanation',
      },
    ],
  },
  {
    id: 'finale_explanation',
    title: "La vraie raison",
    description: "Benours explique pourquoi il a créé tout cela.",
    type: 'dialogue',
    world: 'real',
    dialogueSequence: [
      {
        speaker: "Benours",
        text: "Parce que tu es extraordinaire, Côtelette. Et je voulais que tu le voies toi-même.",
        emotion: 'loving',
      },
      {
        speaker: "Benours",
        text: "Le Chapelier, c'était pour te montrer comment tu aides les gens avec TDAH.",
        emotion: 'explaining',
      },
      {
        speaker: "Benours",
        text: "Vékeuna, pour montrer comment tu aides ceux qui cherchent leur identité.",
        emotion: 'explaining',
      },
      {
        speaker: "Benours",
        text: "Blue, pour la confiance que tu sais reconstruire. Tyrion, pour le syndrome de l'imposteur que tu sais combattre.",
        emotion: 'explaining',
      },
      {
        speaker: "Benours",
        text: "Et Eärwen, pour tous ces enfants qui apprennent différemment et que tu guides chaque jour.",
        emotion: 'proud',
      },
      {
        speaker: "Benours",
        text: "Tu changes des vies, Côtelette. Tu changes MA vie. Tous les jours.",
        emotion: 'emotional',
      },
    ],
    choices: [
      {
        text: "*Le serrer dans ses bras*",
        result: 'emotional',
        nextRiddleId: 'finale_ending',
      },
    ],
  },
  {
    id: 'finale_ending',
    title: "Ensemble",
    description: "La fin du voyage... et le début d'un autre.",
    type: 'dialogue',
    world: 'real',
    dialogueSequence: [
      {
        speaker: "Narrateur",
        text: "Tous les personnages que tu as aidés apparaissent autour de vous, applaudissant.",
        emotion: 'celebratory',
      },
      {
        speaker: "Le Chapelier",
        text: "Pour celle qui m'a appris à me concentrer sur l'essentiel !",
        emotion: 'grateful',
      },
      {
        speaker: "Vékeuna",
        text: "Pour celle qui m'a montré qui je suis vraiment.",
        emotion: 'grateful',
      },
      {
        speaker: "Blue",
        text: "*rugissement affectueux*",
        emotion: 'grateful',
      },
      {
        speaker: "Tyrion",
        text: "Pour celle qui combat les imposteurs... dans nos têtes.",
        emotion: 'grateful',
      },
      {
        speaker: "Eärwen",
        text: "Pour celle qui voit la lumière dans chaque façon d'être.",
        emotion: 'grateful',
      },
      {
        speaker: "Benours",
        text: "Je t'aime, Côtelette. Bon (R)éveil !",
        emotion: 'loving',
      },
    ],
    hint: "Passe en mode SPECTRAL pour voir le message final...",
    choices: [
      {
        text: "Je t'aime aussi, mon Benours chauve préféré.",
        result: 'complete',
        ending: 'true_ending',
      },
    ],
  },
];

/**
 * Obtenir un riddle par son ID
 */
export function getRiddleById(id: string): Riddle | undefined {
  return reveilRiddles.find((r) => r.id === id);
}

/**
 * Obtenir tous les riddles d'un monde spécifique
 */
export function getRiddlesByWorld(worldPrefix: string): Riddle[] {
  return reveilRiddles.filter((r) => r.id.startsWith(worldPrefix));
}

// ==========================================
// DIALOGUES DYNAMIQUES DE L'ORACLE
// ==========================================

/**
 * Dialogues de l'Oracle basés sur la progression du joueur
 * L'Oracle donne des conseils humoristiques sur la quête en cours
 */
export const oracleDynamicDialogues: Record<string, Riddle> = {
  // Premier dialogue - Bienvenue
  oracle_first: {
    id: 'riddle_oracle_welcome',
    npcId: 'obj_oracle',
    npcName: "L'Oracle Chevelu",
    question: `*ajuste sa magnifique perruque*

"Ah ! Te voilà enfin, Côtelette !

Je t'attendais. Les dimensions ont besoin de quelqu'un comme toi.

Tu as un don rare : voir au-delà des apparences, comprendre les cœurs.

Des âmes en détresse attendent dans 5 mondes différents. Seule toi peux les aider.

Regarde autour de toi... les portails vers ces mondes ne sont visibles que dans le monde SPECTRAL où tu te trouves maintenant."`,
    type: 'multiple_choice',
    choices: [
      { id: 'a', text: 'Je suis prête à aider !', isCorrect: true },
      { id: 'b', text: 'Pourquoi moi ?', isCorrect: true },
    ],
    correctResponse: `"Excellente attitude ! Parce que tu es une Éducatrice Spécialisée. Tu comprends que chaque personne est unique. Tu sais voir les forces là où d'autres ne voient que des faiblesses.

Et surtout... quelqu'un qui t'aime beaucoup a demandé mon aide pour te montrer à quel point tu es extraordinaire.

Le premier portail - celui de WONDERLAND - est maintenant actif. Le Chapelier Fou a besoin de toi !"`,
    incorrectResponse: '',
    canRetry: true,
  },

  // Après le premier dialogue - Rappel de la mission Wonderland
  oracle_go_wonderland: {
    id: 'riddle_oracle_go_wonderland',
    npcId: 'obj_oracle',
    npcName: "L'Oracle Chevelu",
    question: `*caresse sa perruque pensivement*

"Encore là, Côtelette ? Le Chapelier t'attend !

Il fait tourner son thé en rond depuis des heures... ou des jours ? Difficile à dire avec lui.

Le portail rose de WONDERLAND est actif - c'est la sphère qui brille près du pilier rose.

*chuchote* Entre nous, il a vraiment besoin de quelqu'un qui comprend comment fonctionne un cerveau... disons... créatif."`,
    type: 'multiple_choice',
    choices: [
      { id: 'a', text: "J'y vais tout de suite !", isCorrect: true },
      { id: 'b', text: "C'est où déjà le portail ?", isCorrect: true },
    ],
    correctResponse: `"Bonne chance ! N'oublie pas : dans le monde du Chapelier, rien n'est vraiment ce qu'il semble être.

Le portail rose brille près du pilier rose, en bas à gauche de la salle."`,
    incorrectResponse: '',
    canRetry: true,
  },

  // Wonderland terminé - Direction Hawkins
  oracle_wonderland_done: {
    id: 'riddle_oracle_wonderland_done',
    npcId: 'obj_oracle',
    npcName: "L'Oracle Chevelu",
    question: `*fait tournoyer sa perruque de joie*

"MAGNIFIQUE ! Tu as aidé le Chapelier à organiser ses pensées !

*imite le Chapelier* 'Un projet à la fois, avec des étapes claires !' Qui l'eut cru ?

Maintenant, une âme plus... tourmentée t'attend. Dans le monde de HAWKINS, une jeune fille nommée Vékeuna se cache.

Elle pense être un monstre à cause de ses pouvoirs. Elle a besoin de comprendre que ses différences sont des forces.

*tapote sa tête chauve sous la perruque* Un peu comme moi avec mes... cheveux."`,
    type: 'multiple_choice',
    choices: [
      { id: 'a', text: 'Vékeuna a besoin de moi !', isCorrect: true },
      { id: 'b', text: 'Où est le portail de Hawkins ?', isCorrect: true },
    ],
    correctResponse: `"Le portail rouge de Hawkins brille maintenant ! Il est en bas à droite de la salle.

Attention, là-bas les choses peuvent être... à l'envers. Littéralement."`,
    incorrectResponse: '',
    canRetry: true,
  },

  // Hawkins terminé - Direction Jurassic
  oracle_hawkins_done: {
    id: 'riddle_oracle_hawkins_done',
    npcId: 'obj_oracle',
    npcName: "L'Oracle Chevelu",
    question: `*essuie une larme sous sa perruque*

"Vékeuna a trouvé son identité grâce à toi ! 'Je suis plus que mes pouvoirs'...

*renifle* Excuse-moi, c'est l'émotion. Ou la poussière dimensionnelle.

BREF ! Direction JURASSIC PARK maintenant. Blue, une vélociraptrice, a du mal à faire confiance aux humains.

On l'a trahie tellement de fois... Elle a besoin de quelqu'un de patient.

*murmure* Et peut-être d'un peu de nourriture. Les raptors sont toujours plus sympas après manger."`,
    type: 'multiple_choice',
    choices: [
      { id: 'a', text: "J'adore les dinosaures !", isCorrect: true },
      { id: 'b', text: 'Comment gagner sa confiance ?', isCorrect: true },
    ],
    correctResponse: `"Le portail vert de Jurassic Park est actif ! En haut à gauche.

La confiance se construit pas à pas. Pas de mouvements brusques, OK ?"`,
    incorrectResponse: '',
    canRetry: true,
  },

  // Jurassic terminé - Direction Winterfell
  oracle_jurassic_done: {
    id: 'riddle_oracle_jurassic_done',
    npcId: 'obj_oracle',
    npcName: "L'Oracle Chevelu",
    question: `*imite un raptor avec ses mains*

"RAWR ! Euh, pardon. Je m'entraîne. Blue fait maintenant partie de ta meute !

Et le bébé raptor qui te suit partout... trop mignon !

Maintenant, direction WINTERFELL. Tu vas rencontrer Tyrion Lannister.

Il se croit imposteur malgré tous ses accomplissements. Le fameux syndrome de l'imposteur...

*regarde sa perruque* Je connais bien ça. Tu crois que quelqu'un remarque que ce ne sont pas mes vrais cheveux ?"`,
    type: 'multiple_choice',
    choices: [
      { id: 'a', text: "Ton secret est bien gardé !", isCorrect: true },
      { id: 'b', text: "Tyrion, le nain intelligent ?", isCorrect: true },
    ],
    correctResponse: `"Exactement ! Il est Main du Roi mais ne se sent jamais à la hauteur.

Le portail bleu de Winterfell brille en haut à droite. Prépare-toi au froid !"`,
    incorrectResponse: '',
    canRetry: true,
  },

  // Winterfell terminé - Direction Terre du Milieu
  oracle_winterfell_done: {
    id: 'riddle_oracle_winterfell_done',
    npcId: 'obj_oracle',
    npcName: "L'Oracle Chevelu",
    question: `*lève sa coupe imaginaire*

"'Je bois et je sais des choses' ! Tyrion a enfin accepté sa propre valeur.

Plus qu'un monde, Côtelette. Tu touches au but !

En TERRE DU MILIEU, une jeune elfe nommée Eärwen a besoin de toi.

Elle apprend différemment des autres elfes et se sent... déficiente.

*soupire* Si seulement quelqu'un pouvait lui montrer que différent ne veut pas dire moins bien..."`,
    type: 'multiple_choice',
    choices: [
      { id: 'a', text: "C'est exactement mon métier !", isCorrect: true },
      { id: 'b', text: "Comment apprend-elle ?", isCorrect: true },
    ],
    correctResponse: `"Ah, Côtelette... Tu es vraiment faite pour ça.

Le portail doré de la Terre du Milieu brille tout en haut, au centre. C'est le dernier avant... la grande révélation."`,
    incorrectResponse: '',
    canRetry: true,
  },

  // Tous les mondes terminés - Teaser finale
  oracle_all_done: {
    id: 'riddle_oracle_all_done',
    npcId: 'obj_oracle',
    npcName: "L'Oracle Chevelu",
    question: `*sa perruque tremble d'émotion*

"Tu... tu as réussi. CINQ mondes. CINQ âmes sauvées !

Le Chapelier organise ses pensées. Vékeuna connaît sa vraie identité.
Blue fait confiance à nouveau. Tyrion s'accepte enfin.
Et Eärwen brille de sa propre lumière.

*voix qui tremble* Il est temps que tu saches... qui je suis vraiment.

*ajuste nerveusement sa perruque* Et pourquoi j'ai créé tout cela pour toi.

Un dernier portail s'est ouvert... celui de la RÉVÉLATION."`,
    type: 'multiple_choice',
    choices: [
      { id: 'a', text: "Qui es-tu vraiment ?", isCorrect: true },
      { id: 'b', text: "*Regarder la perruque avec suspicion*", isCorrect: true },
    ],
    correctResponse: `"*la perruque glisse légèrement*

Tout sera révélé bientôt, Côtelette. Tout.

Le portail final est apparu... quelque part. Tu le trouveras."`,
    incorrectResponse: '',
    canRetry: true,
  },

  // Dialogue par défaut après avoir parlé (rappel de quête en cours)
  oracle_reminder: {
    id: 'riddle_oracle_reminder',
    npcId: 'obj_oracle',
    npcName: "L'Oracle Chevelu",
    question: `*ajuste sa perruque*

"Tu es encore là ? Les portails t'attendent !

N'oublie pas : tu peux voir les portails uniquement en monde SPECTRAL.

*pointe du doigt* Va sauver des âmes, Côtelette !"`,
    type: 'multiple_choice',
    choices: [
      { id: 'a', text: "J'y vais !", isCorrect: true },
    ],
    correctResponse: `"Bonne chance, voyageuse des dimensions !"`,
    incorrectResponse: '',
    canRetry: true,
  },
};

/**
 * Obtient le dialogue approprié de l'Oracle basé sur la progression du joueur
 * @param completedRooms Liste des roomIds complétés
 * @param solvedRiddles Set des riddleIds résolus
 * @returns Le riddle approprié pour l'Oracle
 */
export function getOracleDynamicDialogue(
  completedRooms: string[],
  solvedRiddles: Set<string>
): Riddle {
  // Vérifier si c'est la première interaction (n'a jamais parlé à l'Oracle)
  if (!solvedRiddles.has('riddle_oracle_welcome')) {
    return oracleDynamicDialogues.oracle_first;
  }

  // Vérifier les niveaux complétés dans l'ordre
  const wonderlandDone = completedRooms.includes('room_wonderland_reveil');
  const hawkinsDone = completedRooms.includes('room_hawkins_reveil');
  const jurassicDone = completedRooms.includes('room_jurassic_reveil');
  const winterfellDone = completedRooms.includes('room_winterfell_reveil');
  const middleEarthDone = completedRooms.includes('room_middle_earth_reveil');

  // Tous les mondes terminés
  if (wonderlandDone && hawkinsDone && jurassicDone && winterfellDone && middleEarthDone) {
    return oracleDynamicDialogues.oracle_all_done;
  }

  // Winterfell terminé - Direction Terre du Milieu
  if (winterfellDone) {
    return oracleDynamicDialogues.oracle_winterfell_done;
  }

  // Jurassic terminé - Direction Winterfell
  if (jurassicDone) {
    return oracleDynamicDialogues.oracle_jurassic_done;
  }

  // Hawkins terminé - Direction Jurassic
  if (hawkinsDone) {
    return oracleDynamicDialogues.oracle_hawkins_done;
  }

  // Wonderland terminé - Direction Hawkins
  if (wonderlandDone) {
    return oracleDynamicDialogues.oracle_wonderland_done;
  }

  // A parlé à l'Oracle mais n'a pas encore fait Wonderland
  return oracleDynamicDialogues.oracle_go_wonderland;
}

// ==========================================
// DIALOGUES DYNAMIQUES DU CHAPELIER
// ==========================================

/**
 * Dialogues du Chapelier basés sur la progression du joueur
 * Le Chapelier évolue émotionnellement au fil de l'aide apportée
 */
export const hatterDynamicDialogues: Record<string, Riddle> = {
  // Première rencontre - Le Chapelier anxieux
  hatter_first: {
    id: 'hatter_first_meeting',
    npcId: 'obj_hatter',
    npcName: 'Le Chapelier Fou',
    question: `*tripote nerveusement son chapeau de travers*

"Un thé ! Oui un thé ! Non attends... un gâteau ? OÙ EST MA TASSE ?!

...

*s'arrête soudainement, te regarde*

Oh. Une visiteuse.

*voix tremblante*

Pardon... je... je n'arrive plus à... tout va trop vite dans ma tête.

Avant, j'organisais les MEILLEURES tea parties de tout Wonderland ! Les gens venaient de partout ! Mais maintenant...

*s'effondre sur une chaise*

...maintenant les pensées sont trop nombreuses. Elles tourbillonnent. Je n'arrive plus à les attraper.

Tout le monde dit que je suis fou. Peut-être qu'ils ont raison..."`,
    hint: 'Écoute avec ton cœur, pas juste avec tes oreilles...',
    type: 'multiple_choice',
    choices: [
      { id: 'a', text: 'Calme-toi ! Tu dois juste te concentrer.', isCorrect: false },
      { id: 'b', text: 'Tu n\'es pas fou. Tu penses juste... différemment.', isCorrect: true },
      { id: 'c', text: 'Qu\'est-ce qui a changé pour toi ?', isCorrect: true },
    ],
    correctResponse: `*lève lentement les yeux, surpris*

"Tu... tu ne me trouves pas fou ?"

*une petite lumière s'allume dans son regard*

"Personne ne m'a jamais dit ça avant. Ils disent tous 'tais-toi', 'arrête de bouger', 'concentre-toi'..."

*soupire profondément*

"Tu sais ce qui me manque le plus ? Organiser une tea party. Mais je n'y arrive plus tout seul. Il y a trop d'étapes, trop de choses à penser...

Si seulement quelqu'un pouvait m'aider à rassembler les éléments... Une TASSE, une SOUCOUPE, une THÉIÈRE et un GÂTEAU.

Ils sont quelque part dans le jardin. Si tu pouvais les retrouver... peut-être qu'ensemble, on pourrait réorganiser une tea party ?"

*regarde avec espoir*`,
    incorrectResponse: `*recule, blessé*

"Me... me calmer ? Tu crois que je n'ai pas ESSAYÉ ?!

*les yeux se remplissent de larmes*

C'est ce qu'ils disent tous. 'Calme-toi'. Comme si c'était si simple.

Mon cerveau ne fonctionne pas comme le tien. Ce n'est pas une question de volonté..."

*se détourne*

"Peut-être que tu devrais... essayer de comprendre plutôt que de me dire quoi faire."`,
    canRetry: true,
  },

  // Après avoir parlé mais pas encore tous les items
  hatter_collecting: {
    id: 'hatter_collecting',
    npcId: 'obj_hatter',
    npcName: 'Le Chapelier Fou',
    question: `*lève la tête avec espoir*

"Tu... tu as trouvé quelque chose ?

*regarde tes mains*

Il me faut les 4 éléments pour la tea party :
- Une TASSE (elle doit être près du vieux champignon)
- Une SOUCOUPE (je crois l'avoir vue près des roses)
- Une THÉIÈRE (quelque part vers le labyrinthe)
- Un GÂTEAU (le Lièvre en gardait un, je crois...)

*triture son chapeau*

Prends ton temps. Je... je sais que je t'en demande beaucoup.

Mais s'il te plaît... aide-moi à retrouver la joie des tea parties."`,
    type: 'multiple_choice',
    choices: [
      { id: 'a', text: 'Je continue à chercher !', isCorrect: true },
    ],
    correctResponse: `*sourit faiblement*

"Merci... vraiment. Tu es la première personne à m'écouter depuis longtemps."`,
    incorrectResponse: '',
    canRetry: true,
  },

  // Tous les items collectés - prêt à installer
  hatter_ready_setup: {
    id: 'hatter_ready_setup',
    npcId: 'obj_hatter',
    npcName: 'Le Chapelier Fou',
    question: `*sursaute en voyant les objets dans tes bras*

"Tu... tu as TOUT trouvé ?!

*les yeux brillent d'émotion*

Oh... oh mon dieu. La tasse, la soucoupe, la théière ET le gâteau !

*respire profondément*

D'accord. D'accord, on peut faire ça. Mais... une chose à la fois.

Tu vois les CERCLES MAGIQUES autour de la table ? Place chaque élément sur le bon cercle :

🩷 Cercle ROSE (nord) → La TASSE
💚 Cercle VERT (est) → La SOUCOUPE
💙 Cercle BLEU (sud) → La THÉIÈRE
💜 Cercle VIOLET (ouest) → Le GÂTEAU

*te regarde avec confiance*

Je sais que tu peux le faire. Une étape à la fois. Comme tu m'as appris."`,
    type: 'multiple_choice',
    choices: [
      { id: 'a', text: "Je m'en occupe !", isCorrect: true },
    ],
    correctResponse: `*joint les mains nerveusement*

"Je te fais confiance. Vas-y doucement... une chose à la fois."`,
    incorrectResponse: '',
    canRetry: true,
  },

  // Table partiellement installée
  hatter_installing: {
    id: 'hatter_installing',
    npcId: 'obj_hatter',
    npcName: 'Le Chapelier Fou',
    question: `*observe la table avec attention*

"C'est bien ! Continue comme ça...

*compte sur ses doigts*

Il reste encore des éléments à placer. N'oublie pas :
- Cercle ROSE = Tasse
- Cercle VERT = Soucoupe
- Cercle BLEU = Théière
- Cercle VIOLET = Gâteau

Tu peux y arriver. Une chose à la fois."`,
    type: 'multiple_choice',
    choices: [
      { id: 'a', text: 'Je continue !', isCorrect: true },
    ],
    correctResponse: `*hoche la tête avec encouragement*`,
    incorrectResponse: '',
    canRetry: true,
  },

  // Tout est installé - validation finale
  hatter_validate: {
    id: 'hatter_validate',
    npcId: 'obj_hatter',
    npcName: 'Le Chapelier Fou',
    question: `*s'approche de la table, les mains tremblantes*

"Est-ce que... est-ce que c'est prêt ?"

*inspecte chaque élément*

"La tasse... la soucoupe... la théière... le gâteau..."

*les larmes coulent sur ses joues*

"C'est... c'est PARFAIT. Exactement comme avant. Non... MIEUX qu'avant !

*se tourne vers toi*

Tu m'as aidé à faire quelque chose que je pensais impossible. Tu ne m'as pas dit de me calmer. Tu ne m'as pas dit que j'étais fou.

Tu m'as ÉCOUTÉ. Et tu m'as aidé, étape par étape.

*retire son chapeau*

Ce chapeau... il est spécial. Je l'ai fait le jour où j'ai compris que mon cerveau était un don, pas une malédiction.

Je voudrais que tu l'aies. Pour te rappeler qu'il n'y a pas de 'bonne' façon de penser."`,
    type: 'multiple_choice',
    choices: [
      { id: 'a', text: 'Merci, Chapelier. Je n\'oublierai jamais cette leçon.', isCorrect: true },
      { id: 'b', text: 'Tu n\'es pas fou. Tu es extraordinaire.', isCorrect: true },
    ],
    correctResponse: `*sourit - un vrai sourire, pour la première fois*

"Et toi, tu es une vraie amie.

Maintenant... QUI VEUT DU THÉ ?!"

*éclate de rire, un rire joyeux et libéré*

🎉 Tu as aidé le Chapelier à retrouver sa joie !

Le PORTAIL DE VICTOIRE s'est ouvert. Tu peux retourner au Sanctuaire quand tu es prête.`,
    incorrectResponse: '',
    canRetry: true,
    onCorrect: [
      {
        type: 'addToInventory',
        params: {
          itemId: 'chapeau_chapelier',
          itemName: 'Chapeau du Chapelier',
          itemDescription: 'Un chapeau fou qui rappelle que la différence est une force.',
          itemIcon: '🎩',
        },
      },
    ],
  },

  // Après avoir terminé (si le joueur revient parler)
  hatter_done: {
    id: 'hatter_done',
    npcId: 'obj_hatter',
    npcName: 'Le Chapelier Fou',
    question: `*sirote son thé joyeusement*

"Ah, Côtelette ! Tu veux du thé ? J'en ai fait 17 sortes différentes !

*rit*

Je plaisante. J'en ai fait 3. Une à la fois. Comme tu m'as appris.

Merci encore. Grâce à toi, je sais maintenant que mon cerveau n'est pas mon ennemi.

Il fonctionne juste... différemment. Et c'est merveilleux."`,
    type: 'multiple_choice',
    choices: [
      { id: 'a', text: 'Je suis contente pour toi, Chapelier !', isCorrect: true },
    ],
    correctResponse: `*lève sa tasse*

"À la différence ! Et aux amis qui nous comprennent !"`,
    incorrectResponse: '',
    canRetry: true,
  },
};

/**
 * Obtient le dialogue approprié du Chapelier basé sur la progression du joueur
 * @param solvedRiddles Set des riddleIds résolus
 * @param inventory Liste des items en inventaire
 * @param placedItems Items placés sur les dalles (via solvedRiddles tea_placed_*)
 * @returns Le riddle approprié pour le Chapelier
 */
export function getHatterDynamicDialogue(
  solvedRiddles: Set<string>,
  inventory: { id: string }[],
  questComplete: boolean
): Riddle {
  // Si la quête est terminée
  if (questComplete || solvedRiddles.has('hatter_validate')) {
    return hatterDynamicDialogues.hatter_done;
  }

  // Vérifier si c'est la première interaction
  if (!solvedRiddles.has('hatter_first_meeting')) {
    return hatterDynamicDialogues.hatter_first;
  }

  // Vérifier les items collectés
  const teaItems = ['tea_item_cup', 'tea_item_saucer', 'tea_item_teapot', 'tea_item_cake'];
  const collectedItems = teaItems.filter(item => inventory.some(i => i.id === item));
  const hasAllItems = collectedItems.length === 4;

  // Vérifier les items placés
  const placedItems = ['tea_placed_cup', 'tea_placed_saucer', 'tea_placed_teapot', 'tea_placed_cake'];
  const placedCount = placedItems.filter(id => solvedRiddles.has(id)).length;
  const allPlaced = placedCount === 4;

  // Tous les items placés - validation
  if (allPlaced) {
    return hatterDynamicDialogues.hatter_validate;
  }

  // Certains items placés - encouragement
  if (placedCount > 0) {
    return hatterDynamicDialogues.hatter_installing;
  }

  // Tous les items collectés - prêt à installer
  if (hasAllItems) {
    return hatterDynamicDialogues.hatter_ready_setup;
  }

  // En train de collecter
  return hatterDynamicDialogues.hatter_collecting;
}

// ============================================
// DIALOGUES DU CHAPELIER - MONDE SPECTRAL
// Après la validation de la tea party, le Chapelier
// apparaît dans le monde spectral pour une conclusion
// ============================================

export const hatterSpectralDialogues: Record<string, Riddle> = {
  // Premier dialogue spectral - Le Chapelier apaisé
  hatter_spectral_first: {
    id: 'hatter_spectral_first',
    npcId: 'obj_hatter_spectral',
    npcName: 'Le Chapelier Apaisé',
    question: `*Le Chapelier brille d'une lumière douce. Son chapeau est droit, ses yeux sont calmes*

"Oh... tu es venue jusqu'ici ? Dans ce monde des pensées ?"

*il sourit, un vrai sourire*

"Je me sens... différent. Avant, cet endroit n'était que ténèbres et doutes.
Maintenant, je vois les bons souvenirs aussi."

*il ajuste son chapeau avec fierté*

"Cette tea party... c'était parfait parce qu'on l'a faite ENSEMBLE.
Je n'ai pas besoin d'être parfait. J'ai besoin d'être... accompagné."

*ses yeux brillent*

"Merci, Côtelette. Tu m'as appris quelque chose d'important."`,
    hint: 'Écoute ce que le Chapelier a appris',
    type: 'multiple_choice',
    choices: [
      { id: 'a', text: "C'était un plaisir de t'aider !", isCorrect: true },
      { id: 'b', text: 'Tu vas organiser d\'autres tea parties ?', isCorrect: true },
      { id: 'c', text: 'Je reviendrai te voir !', isCorrect: true },
    ],
    correctResponse: `*le Chapelier rayonne*

"Tu sais quoi ? Je vais essayer. Petit à petit.
Une tasse d'abord, puis peut-être deux invités... pas besoin de dix !

*il enlève son chapeau et fait une révérence*

"La vraie magie, c'est de faire un pas à la fois.
Et d'accepter l'aide quand elle se présente."

*une lumière dorée l'entoure*

"Retourne au Sanctuaire, petite éducatrice.
L'Oracle doit avoir hâte de te raconter la suite !"

🎓 Tu as appris : L'ÉCOUTE ACTIVE
Parfois, aider quelqu'un c'est juste être là, pas tout résoudre.`,
    incorrectResponse: '',
    canRetry: false,
  },

  // Dialogue si le joueur revient
  hatter_spectral_done: {
    id: 'hatter_spectral_done',
    npcId: 'obj_hatter_spectral',
    npcName: 'Le Chapelier Apaisé',
    question: `*le Chapelier fait tourner sa tasse de thé, souriant*

"Ah, tu reviens me voir ! C'est gentil."

*il s'assoit confortablement*

"Je prépare déjà ma prochaine tea party. Juste trois invités cette fois.
Pas de pression !"

*il lève sa tasse vers toi*

"À l'amitié, et aux petits pas !"`,
    hint: '',
    type: 'multiple_choice',
    choices: [
      { id: 'a', text: 'À l\'amitié ! 🍵', isCorrect: true },
    ],
    correctResponse: '*Le Chapelier sourit et boit son thé*\n\n"Continue ton voyage, petite éducatrice. On se reverra !"',
    incorrectResponse: '',
    canRetry: false,
  },
};

/**
 * Retourne le dialogue approprié pour le Chapelier dans le monde spectral
 * @param solvedRiddles Set des riddles résolus
 * @returns Le riddle approprié pour le Chapelier spectral
 */
export function getHatterSpectralDialogue(solvedRiddles: Set<string>): Riddle {
  // Si déjà parlé au Chapelier spectral
  if (solvedRiddles.has('hatter_spectral_first')) {
    return hatterSpectralDialogues.hatter_spectral_done;
  }

  // Première interaction dans le monde spectral
  return hatterSpectralDialogues.hatter_spectral_first;
}

// ==========================================
// DIALOGUES DYNAMIQUES DE HAWKINS
// ==========================================

/**
 * Dialogues pour Dustin, Erica et les quêtes de Hawkins
 */
export const hawkinsDynamicDialogues: Record<string, Riddle> = {
  // Première rencontre avec Dustin
  dustin_first_contact: {
    id: 'dustin_first_contact',
    npcId: 'obj_dustin',
    npcName: 'Dustin',
    question: `*ajuste sa casquette et te regarde avec des yeux écarquillés*

"Attends... tu vois Vékeuna toi aussi ?! Tu sais ce qui se passe ici ?!

*regarde autour de lui nerveusement*

"On était une bande. Mike, Lucas, Erica, et... Vékeuna. Il était un peu bizarre, mais c'était notre ami.

*sa voix tremble*

"Un jour, des gamins l'ont trop harcelé. Il... il a pété les plombs. Des trucs ont volé. Les lumières ont explosé. Tout le monde a eu peur...

*baisse la tête*

"...on a eu peur aussi. On s'est enfuis. On a abandonné notre ami quand il avait le plus besoin de nous."

*te regarde avec espoir*

"Tu peux nous aider à le retrouver ? Il doit être dans l'Upside Down..."`,
    hint: 'Dustin a besoin d\'aide pour réparer son erreur.',
    type: 'multiple_choice',
    choices: [
      { id: 'a', text: 'Bien sûr ! On va le retrouver ensemble.', isCorrect: true },
      { id: 'b', text: 'Pourquoi vous l\'avez abandonné ?', isCorrect: true },
      { id: 'c', text: 'Qu\'est-ce que l\'Upside Down ?', isCorrect: true },
    ],
    correctResponse: `*le visage de Dustin s'illumine*

"Merci ! Vraiment, merci !

*sort un walkie-talkie de sa poche*

"Avec ça, on peut communiquer entre les mondes. Mais d'abord...

*se gratte la tête*

"On a un problème. Quand on a fui, on a laissé nos vélos partout. Les autres refusent de bouger tant qu'on ne les a pas récupérés.

Lucas a laissé le sien près de l'école. Mike l'a jeté dans les buissons près de la maison de Vékeuna.

Si tu pouvais les trouver et les ramener chez moi, on pourrait commencer le plan !"

📍 QUÊTE : Retrouve les vélos perdus et ramène-les à la MAISON DE DUSTIN.`,
    incorrectResponse: `*Dustin baisse les yeux*

"Je sais... on aurait dû rester. Mais on était des gamins. On avait peur...

*soupire*

"C'est pour ça qu'on veut réparer notre erreur. Tu veux bien nous aider ?"`,
    canRetry: true,
    onCorrect: [
      { type: 'solveRiddle', params: { riddleId: 'dustin_first_contact' } },
    ],
  },

  // Dustin après avoir parlé une première fois (pendant la collecte de vélos)
  dustin_collecting: {
    id: 'dustin_collecting',
    npcId: 'obj_dustin',
    npcName: 'Dustin',
    question: `*Dustin fait les cent pas*

"Tu as trouvé les vélos ?

- Le vélo BLEU de Lucas est près de l'école
- Le vélo JAUNE de Mike est dans les buissons près de la maison de Vékeuna

Ramène-les à ma MAISON et on pourra passer à la suite !"`,

    hint: '',
    type: 'multiple_choice',
    choices: [
      { id: 'a', text: 'Je continue à chercher !', isCorrect: true },
    ],
    correctResponse: '',
    incorrectResponse: '',
    canRetry: false,
  },

  // Dustin après que les vélos sont ramenés
  dustin_quest_complete: {
    id: 'dustin_quest_complete',
    npcId: 'obj_dustin',
    npcName: 'Dustin',
    question: `*Dustin sourit*

"Génial, les vélos sont revenus ! Tu as vraiment assuré !

*regarde vers la faille dimensionnelle*

Maintenant, la faille est ouverte. Tu peux aller parler à Erica pour en savoir plus sur Vékeuna, et ensuite entrer dans la faille pour le rejoindre dans l'Upside Down.

📍 La FAILLE DIMENSIONNELLE t'attend !

Et n'oublie pas de chercher le Walkie-Talkie de Vékeuna, ça pourrait servir..."`,
    hint: '',
    type: 'multiple_choice',
    choices: [
      { id: 'a', text: 'Compris !', isCorrect: true },
    ],
    correctResponse: '"Super ! Fonce !"',
    incorrectResponse: '',
    canRetry: false,
  },

  // Râtelier - quand le joueur est sur un vélo (obsolète - utiliser getBikeRackDialogue pour génération dynamique)
  bike_rack_deposit: {
    id: 'bike_rack_deposit',
    npcId: 'obj_bike_rack',
    npcName: 'Râtelier à Vélos',
    question: `*Tu arrives au râtelier avec ton vélo*

Veux-tu déposer ton vélo ici ?`,
    hint: 'Dépose le vélo pour libérer tes mains.',
    type: 'multiple_choice',
    choices: [
      { id: 'a', text: 'Oui, déposer le vélo', isCorrect: true },
      { id: 'b', text: 'Non, je garde le vélo', isCorrect: false },
    ],
    correctResponse: `*Tu descends du vélo et l'accroches au râtelier*

🚲 Vélo déposé !`,
    incorrectResponse: `*Tu décides de garder le vélo pour l'instant*

D'accord, continue à explorer !`,
    canRetry: false,
    onCorrect: [
      { type: 'dismountBike', params: {} },
    ],
  },

  // Râtelier - quand Dustin n'a pas encore été rencontré
  bike_rack_not_ready: {
    id: 'bike_rack_not_ready',
    npcId: 'obj_bike_rack',
    npcName: 'Râtelier à Vélos',
    question: `*Tu examines le râtelier à vélos*

Il y a des emplacements pour plusieurs vélos, mais ils sont vides.

*Tu entends quelqu'un parler près d'une maison...*

Peut-être devrais-tu aller voir qui c'est avant de t'occuper des vélos ?

📍 Va parler à DUSTIN près de sa maison.`,
    hint: 'Dustin semble avoir besoin d\'aide.',
    type: 'multiple_choice',
    choices: [
      { id: 'a', text: 'D\'accord, je vais voir Dustin.', isCorrect: true },
    ],
    correctResponse: '',
    incorrectResponse: '',
    canRetry: false,
  },

  // Râtelier - quand le joueur n'est pas sur un vélo
  bike_rack_empty: {
    id: 'bike_rack_empty',
    npcId: 'obj_bike_rack',
    npcName: 'Râtelier à Vélos',
    question: `*Tu examines le râtelier à vélos*

Il y a des emplacements pour plusieurs vélos.

Tu n'as pas de vélo pour l'instant. Trouve les vélos perdus de Lucas (bleu) et Mike (jaune) !`,
    hint: 'Trouve les vélos perdus.',
    type: 'multiple_choice',
    choices: [
      { id: 'a', text: 'D\'accord !', isCorrect: true },
    ],
    correctResponse: '',
    incorrectResponse: '',
    canRetry: false,
  },

  // Râtelier - quand tous les vélos sont déposés (rappel)
  bike_rack_complete: {
    id: 'bike_rack_complete',
    npcId: 'obj_bike_rack',
    npcName: 'Râtelier à Vélos',
    question: `*Le râtelier contient les deux vélos*

🚲🚲 Les vélos de Lucas et Mike sont bien rangés.

💡 N'oublie pas :
• Parle à ERICA près de l'école
• Entre dans la FAILLE DIMENSIONNELLE`,
    hint: '',
    type: 'multiple_choice',
    choices: [
      { id: 'a', text: 'Compris !', isCorrect: true },
    ],
    correctResponse: '',
    incorrectResponse: '',
    canRetry: false,
  },

  // Ancien dialogue (gardé pour compatibilité)
  bike_return_check: {
    id: 'bike_return_check',
    npcId: 'obj_bike_rack',
    npcName: 'Râtelier à Vélos',
    question: `*Tu te tiens devant le râtelier à vélos*

Tu as les vélos dans ton inventaire ?`,
    hint: 'Assure-toi d\'avoir collecté les deux vélos perdus.',
    type: 'inventory_check',
    requiredItems: ['bike_blue', 'bike_yellow'],
    successMessage: `*Tu accroches les vélos au râtelier*

🚲 Les vélos sont de retour !

*Dustin accourt*

"Génial ! Maintenant, regarde vers la maison de Vékeuna...

*une lueur rouge apparaît dans le ciel*

"La faille ! Elle s'ouvre ! C'est notre chance de le rejoindre dans l'Upside Down !"

📍 La FAILLE DIMENSIONNELLE est maintenant accessible !`,
    failureMessage: `*Le râtelier est vide*

Il te manque des vélos. Cherche :
- Le vélo BLEU de Lucas (près de l'école)
- Le vélo JAUNE de Mike (dans les buissons)`,
    onSuccess: [
      { type: 'solveRiddle', params: { riddleId: 'dustin_bike_quest_done' } },
      { type: 'removeFromInventory', params: { itemId: 'bike_blue' } },
      { type: 'removeFromInventory', params: { itemId: 'bike_yellow' } },
    ],
  },

  // Erica avant que Dustin soit aidé - elle renvoie vers Dustin
  erica_not_ready: {
    id: 'erica_not_ready',
    npcId: 'obj_erica',
    npcName: 'Erica',
    question: `*croise les bras avec attitude et te regarde de haut en bas*

"Hé, toi là ! Tu veux aider Vékeuna ?

*lève un sourcil*

"Cool. Mais d'abord, va voir Dustin. C'est LUI qui a le plan.

*fait un geste de la main vers la maison de Dustin*

"Il est là-bas, près de sa maison. Il fait les cent pas depuis des heures en se triturant les méninges.

*soupire*

"Moi je suis prête depuis le début, mais MONSIEUR veut d'abord récupérer les vélos. Comme si c'était le moment de penser au matériel...

Enfin bref. Va le voir LUI d'abord. Après on parlera."`,
    hint: 'Va parler à Dustin d\'abord.',
    type: 'multiple_choice',
    choices: [
      { id: 'a', text: 'D\'accord, je vais voir Dustin.', isCorrect: true },
      { id: 'b', text: 'Pourquoi tu ne peux pas m\'aider directement ?', isCorrect: true },
    ],
    correctResponse: `*Erica hausse les épaules*

"Parce que Dustin a le PLAN. Et les walkie-talkies. Et les cartes. Et...

*lève les yeux au ciel*

"Bref, il a tout organisé. Moi je suis juste là pour le soutien moral. Et pour dire aux gens qu'ils sont des idiots quand ils font des trucs idiots.

*te fait un signe de tête*

"Allez, file ! Dustin t'attend."`,
    incorrectResponse: '',
    canRetry: true,
  },

  // Erica quand les vélos ne sont pas encore récupérés
  erica_bikes_not_done: {
    id: 'erica_bikes_not_done',
    npcId: 'obj_erica',
    npcName: 'Erica',
    question: `*Erica te regarde avec impatience*

"Attends, t'as pas encore ramené les vélos ?

*croise les bras*

"Dustin t'a demandé de récupérer les vélos de Lucas et Mike, non ? Fais ça d'abord !

*pointe vers le quartier*

"Le vélo BLEU de Lucas est quelque part par là. Et le JAUNE de Mike aussi.

Ramène-les au râtelier près de chez Dustin et APRÈS on parlera du vrai plan !"`,
    hint: 'Récupère les vélos et ramène-les au râtelier.',
    type: 'multiple_choice',
    choices: [
      { id: 'a', text: 'D\'accord, je vais chercher les vélos.', isCorrect: true },
    ],
    correctResponse: `*Erica hoche la tête*

"Voilà ! C'est pas compliqué.

Vélo BLEU, vélo JAUNE, râtelier. Easy !"`,
    incorrectResponse: '',
    canRetry: true,
  },

  // Première rencontre avec Erica (après Dustin) - Introduction
  erica_first_contact: {
    id: 'erica_first_contact',
    npcId: 'obj_erica',
    npcName: 'Erica',
    question: `*croise les bras avec attitude*

"Oh, ENCORE quelqu'un qui veut aider Vékeuna.

*lève un sourcil*

"Tu sais quoi ? Au moins toi tu fais quelque chose. Pas comme ces crétins qui ont fui.

*soupire*

"Moi j'étais pas là ce jour-là. Mais si j'avais été là, je serais RESTÉE. On abandonne pas ses amis, point final.

*sort un walkie-talkie de sa poche*

"Pour communiquer avec Vékeuna dans l'Upside Down, on utilise un code secret en morse. C'est notre truc à nous, le Party.

*te montre le walkie-talkie*

"Je vais t'apprendre le code de l'AMITIÉ. C'est le mot de passe pour que Vékeuna sache qu'on est de vrais amis."`,
    hint: 'Erica va t\'apprendre le code secret.',
    type: 'multiple_choice',
    choices: [
      { id: 'a', text: 'Je suis prêt(e) à apprendre !', isCorrect: true },
    ],
    correctResponse: `*Erica hoche la tête avec un sourire*

"OK, écoute bien. C'est pas compliqué mais faut pas se tromper.

Le code AMI en morse simplifié, c'est :

●━━●

Court - Long - Long - Court

*te tend le walkie-talkie*

"À ton tour ! Tape le code sur le walkie-talkie."`,
    incorrectResponse: '',
    canRetry: false,
    onCorrect: [
      { type: 'solveRiddle', params: { riddleId: 'erica_first_contact' } },
    ],
  },

  // Puzzle d'Erica - Le Walkie-Talkie
  erica_friendship_puzzle: {
    id: 'erica_friendship_puzzle',
    npcId: 'obj_erica',
    npcName: 'Erica',
    question: `*Erica te regarde avec impatience*

"Allez ! Tape le code AMI sur le walkie-talkie !

●━━●

Court - Long - Long - Court

*te montre les boutons*

"Le bouton BLEU pour un signal COURT (●)
Le bouton ROSE pour un signal LONG (━)"`,
    hint: 'Rappelle-toi : ●━━● = Court, Long, Long, Court',
    type: 'walkie_puzzle',
    walkiePuzzleConfig: {
      targetCode: 'SLLS',
      hint: 'AMI = ●━━● (Court-Long-Long-Court)',
      successMessage: 'Code accepté ! Vékeuna peut maintenant t\'entendre !',
    },
    choices: [
      { id: 'a', text: 'Ouvrir le Walkie-Talkie', isCorrect: true },
    ],
    correctResponse: '',
    incorrectResponse: '',
    canRetry: true,
  },

  // Erica après le puzzle walkie-talkie réussi
  erica_walkie_success: {
    id: 'erica_walkie_success',
    npcId: 'obj_erica',
    npcName: 'Erica',
    question: `*Les yeux d'Erica s'illuminent*

"T'as réussi ! Vékeuna a reçu ton signal !

*sourit pour la première fois*

"Tu sais quoi ? T'es pas si mal finalement. Tu comprends ce que ça veut dire d'être un ami.

*pointe vers la faille dimensionnelle*

"Maintenant, va le rejoindre dans l'Upside Down. La faille est ouverte. Et... dis-lui qu'on l'attend. Tous.

📍 Entre dans la FAILLE DIMENSIONNELLE !"`,
    hint: '',
    type: 'multiple_choice',
    choices: [
      { id: 'a', text: 'J\'y vais !', isCorrect: true },
    ],
    correctResponse: '"Bonne chance. Et... merci."',
    incorrectResponse: '',
    canRetry: false,
    onCorrect: [
      { type: 'solveRiddle', params: { riddleId: 'erica_puzzle_done' } },
    ],
  },

  // Erica après le puzzle - rappel
  erica_after_puzzle: {
    id: 'erica_after_puzzle',
    npcId: 'obj_erica',
    npcName: 'Erica',
    question: `*Erica te regarde avec un petit sourire*

"Tu as compris. C'est bien.

*redevient sérieuse*

"N'oublie pas : le walkie-talkie est près de la maison de Vékeuna. Trouve-le, et ensuite... va dans la faille.

Vékeuna a besoin de quelqu'un qui reste. Quelqu'un qui comprend.

*te fait un signe de tête*

"Allez, bouge ! Il t'attend."`,
    hint: '',
    type: 'multiple_choice',
    choices: [
      { id: 'a', text: 'J\'y vais !', isCorrect: true },
    ],
    correctResponse: '"Bonne chance."',
    incorrectResponse: '',
    canRetry: false,
  },

  // Interaction avec la faille
  rift_enter: {
    id: 'rift_enter',
    npcId: 'obj_dimensional_rift',
    npcName: 'Faille Dimensionnelle',
    question: `*La faille pulse d'une lueur rouge sang*

*KRRRRR... KRRRRR...*

Une énergie étrange émane de la déchirure dans la réalité. De l'autre côté, tu aperçois un monde sombre, couvert de spores...

L'Upside Down.

C'est là que Vékeuna s'est réfugié, rongé par la colère et la tristesse.

*une voix résonne faiblement depuis l'intérieur*

"...partez... je suis... dangereux..."`,
    hint: 'La faille mène à l\'Upside Down où se trouve Vékeuna.',
    type: 'multiple_choice',
    choices: [
      { id: 'a', text: 'Vékeuna ! Je viens t\'aider !', isCorrect: true },
      { id: 'b', text: '*Entrer dans la faille*', isCorrect: true },
    ],
    correctResponse: `*Tu traverses la faille*

🌀 CHANGEMENT DE MONDE

*L'air devient froid et humide. Des particules flottent autour de toi. Le monde est une version sombre et corrompue de Hawkins.*

Tu es dans l'UPSIDE DOWN.

*Au loin, une silhouette entourée d'énergie rouge...*

"PARTEZ ! Je ne contrôle plus rien !"

C'est Vékeuna. Il a besoin de ton aide.

📍 Approche-toi de Vékeuna dans le monde SPECTRAL.`,
    incorrectResponse: '',
    canRetry: true,
    onCorrect: [
      { type: 'solveRiddle', params: { riddleId: 'rift_opened' } },
      { type: 'switchWorld', params: { world: 'spectral' } },
    ],
  },

  // Faille de retour (Upside Down vers monde réel)
  rift_return: {
    id: 'rift_return',
    npcId: 'obj_dimensional_rift_spectral',
    npcName: 'Faille Dimensionnelle',
    question: `*La faille pulse d'une lueur bleutée*

*KRRRRR... KRRRRR...*

De l'autre côté, tu aperçois la banlieue de Hawkins baignée dans la lumière du jour.

Le monde réel.

*l'air de l'Upside Down est lourd et oppressant*`,
    hint: 'La faille te permet de retourner dans le monde réel.',
    type: 'multiple_choice',
    choices: [
      { id: 'a', text: 'Traverser la faille vers le monde réel', isCorrect: true },
      { id: 'b', text: 'Rester dans l\'Upside Down pour l\'instant', isCorrect: false },
    ],
    correctResponse: `*Tu traverses la faille*

🌀 CHANGEMENT DE MONDE

*La lumière t'éblouit un instant. L'air est frais et normal. Tu es de retour à Hawkins.*

Tu peux toujours utiliser la faille pour retourner dans l'Upside Down.`,
    incorrectResponse: `*Tu restes dans l'Upside Down*

L'atmosphère est lourde, mais tu sens que tu dois encore explorer ce lieu...`,
    canRetry: true,
    onCorrect: [
      { type: 'switchWorld', params: { world: 'real' } },
    ],
  },

  // Puzzle du walkie-talkie
  walkie_talkie_puzzle: {
    id: 'walkie_talkie_puzzle',
    npcId: 'obj_walkie_station',
    npcName: 'Station de Communication',
    question: `*Tu actives le walkie-talkie de Vékeuna*

*KRRR... KRRR...*

"...allô ? Il y a quelqu'un ?"

*la voix de Vékeuna, tremblante*

"Je... je vous entends. Qui est là ?"

*tu dois choisir quoi lui dire pour établir le contact*`,
    hint: 'Parle avec ton cœur, pas avec ta peur.',
    type: 'multiple_choice',
    choices: [
      { id: 'a', text: 'Vékeuna, tes amis sont là. Ils regrettent de t\'avoir abandonné.', isCorrect: true },
      { id: 'b', text: 'Tu dois te calmer et sortir de là !', isCorrect: false },
      { id: 'c', text: 'On ne te juge pas. On veut juste t\'aider.', isCorrect: true },
    ],
    correctResponse: `*un long silence*

*KRRR...*

"...vraiment ? Ils... ils regrettent ?"

*sa voix devient moins agressive*

"J'ai... j'ai fait des choses horribles. J'ai fait peur à tout le monde. Comment ils pourraient me pardonner ?"

*KRRR...*

"...tu peux leur dire que... que je suis désolé ?"

💡 Tu as établi un premier contact avec Vékeuna !
Va le voir dans l'Upside Down pour continuer à l'aider.`,
    incorrectResponse: `*le walkie-talkie grésille violemment*

"ME CALMER ?! Tu crois que j'ai pas ESSAYÉ ?!"

*bruit de parasites*

"Vous êtes tous pareils... vous ne comprenez rien..."

*la communication se coupe*

Essaie une autre approche...`,
    canRetry: true,
    onCorrect: [
      { type: 'solveRiddle', params: { riddleId: 'walkie_contact_made' } },
    ],
  },

  // Confrontation avec Vékeuna
  vekeuna_first_contact: {
    id: 'vekeuna_first_contact',
    npcId: 'obj_vekeuna',
    npcName: 'Vékeuna',
    question: `*Vékeuna flotte dans les airs, entouré d'une aura rouge crépitante*

"NE... NE T'APPROCHE PAS !"

*des débris volent autour de lui*

"Tu ne comprends pas... je suis un MONSTRE ! Tout le monde le dit !

*sa voix se brise*

"Mes amis... ils ont fui. Ils avaient PEUR de moi. Même eux...

*des larmes coulent sur ses joues*

"Je ne voulais pas... je ne voulais pas leur faire peur. Mais ma colère... elle est sortie toute seule.

Et maintenant... maintenant je suis coincé ici. Dans ce monde horrible. Avec tous ces souvenirs..."

*les mots "MONSTRE", "FREAK" apparaissent en néons rouges autour de lui*`,
    hint: 'Vékeuna a besoin de comprendre qu\'il n\'est pas défini par sa colère.',
    type: 'multiple_choice',
    choices: [
      { id: 'a', text: 'Tu n\'es pas un monstre. Tu as juste... des émotions très fortes.', isCorrect: true },
      { id: 'b', text: 'Tes amis regrettent. Ils sont là, dehors, qui t\'attendent.', isCorrect: true },
      { id: 'c', text: 'Tu dois contrôler ta colère !', isCorrect: false },
    ],
    correctResponse: `*Vékeuna s'arrête. L'aura rouge vacille*

"...ils... ils regrettent ?"

*redescend lentement vers le sol*

"Tout le monde me disait de me contrôler. De me calmer. Personne n'a jamais dit que mes émotions étaient... normales.

*te regarde*

"Tu... tu me trouves pas bizarre ?"

*les néons "MONSTRE" commencent à s'effacer*

"La colère... elle est toujours là. Mais peut-être... peut-être que je peux apprendre à vivre avec ?

*une lumière dorée commence à percer l'obscurité*

"Tu veux bien... rester avec moi ? Juste un peu ?"

🎓 Tu as appris : LA PATIENCE FACE À LA COLÈRE
La colère n'est pas mauvaise. C'est comment on la gère qui compte.`,
    incorrectResponse: `*l'aura de Vékeuna explose*

"CONTRÔLER ?! TU CROIS QUE J'AI PAS ESSAYÉ ?!"

*des objets volent dans tous les sens*

"C'est TOUJOURS la même chose ! 'Contrôle-toi, Vékeuna', 'Calme-toi, Vékeuna'...

Personne ne comprend que c'est PAS SI SIMPLE !"

*se replie sur lui-même*

Essaie de l'aborder avec plus de compassion...`,
    canRetry: true,
    onCorrect: [
      { type: 'solveRiddle', params: { riddleId: 'vekeuna_helped' } },
    ],
  },

  // Vékeuna apaisé - dialogue final
  vekeuna_resolved: {
    id: 'vekeuna_resolved',
    npcId: 'obj_vekeuna',
    npcName: 'Vékeuna',
    question: `*Vékeuna, maintenant calme, te regarde avec gratitude*

"Merci... vraiment. Personne ne m'avait jamais écouté comme ça.

*regarde ses mains*

"Mes pouvoirs... ils font partie de moi. Comme mes émotions. Je ne peux pas les supprimer. Mais peut-être que je peux apprendre à les... canaliser ?

*te sourit*

"Tu sais, je vais retourner voir mes amis. Leur parler. Vraiment parler.

Et peut-être... peut-être qu'on peut reconstruire quelque chose ensemble."

*te tend un objet brillant*

"Tiens. C'est mon dé D20 porte-bonheur. Pour te remercier."`,
    hint: '',
    type: 'multiple_choice',
    choices: [
      { id: 'a', text: 'Tu es plus fort que tu ne le penses, Vékeuna.', isCorrect: true },
      { id: 'b', text: 'Bonne chance avec tes amis !', isCorrect: true },
    ],
    correctResponse: `*Vékeuna sourit - un vrai sourire*

"Friends don't lie... c'est ce qu'on se disait toujours.

*l'Upside Down commence à se dissiper autour de lui*

"Je crois que je suis prêt à rentrer."

🎉 Tu as aidé Vékeuna à accepter ses émotions !

*Une lumière dorée enveloppe la zone*

Le PORTAIL DE RETOUR s'est ouvert. Retourne au Sanctuaire pour découvrir la suite de ton aventure.`,
    incorrectResponse: '',
    canRetry: false,
    onCorrect: [
      {
        type: 'addToInventory',
        params: {
          itemId: 'de_d20_vekeuna',
          itemName: 'Dé D20 de Vékeuna',
          itemDescription: 'Un dé porte-bonheur qui rappelle que les émotions fortes peuvent devenir une force.',
          itemIcon: '🎲',
        },
      },
      { type: 'completeRoom', params: { roomId: 'room_hawkins_reveil' } },
    ],
  },

  // ============================================
  // DEMAGORDON - Le Boss Démagogue
  // ============================================

  // Première rencontre avec le Demagordon
  demagordon_intro: {
    id: 'demagordon_intro',
    npcId: 'obj_demagordon',
    npcName: 'Le Demagordon',
    question: `*Une créature monstrueuse se dresse devant toi, mais au lieu de rugir, elle... parle avec une voix suave de politicien*

"Ah ! Un visiteur du monde RÉEL ! Bienvenue, bienvenue !

*ajuste une cravate imaginaire*

"Je suis le Demagordon, et j'ai un MESSAGE IMPORTANT pour toi !

*prend une pose théâtrale*

"Le monde réel, c'est POURRI ! Les élites vous mentent ! Les médias vous manipulent ! Mais ICI, dans l'Upside Down, on est LIBRES !

*fait un grand geste*

"Rejoins-moi, et ensemble, on va DÉTRUIRE ce monde corrompu ! On va faire l'Upside Down GREAT AGAIN !"`,
    hint: 'Ce type a l\'air d\'un charlatan...',
    type: 'multiple_choice',
    choices: [
      { id: 'a', text: 'Euh... tu racontes n\'importe quoi.', isCorrect: true },
      { id: 'b', text: 'C\'est quoi ce délire ?', isCorrect: true },
    ],
    correctResponse: '',
    incorrectResponse: '',
    canRetry: false,
    onCorrect: [
      { type: 'solveRiddle', params: { riddleId: 'demagordon_intro' } },
    ],
  },

  // Deuxième phase - Promesses impossibles
  demagordon_promises: {
    id: 'demagordon_promises',
    npcId: 'obj_demagordon',
    npcName: 'Le Demagordon',
    question: `*Le Demagordon s'énerve*

"Tu ne COMPRENDS PAS ! Écoute mes PROMESSES !

*compte sur ses griffes*

"PREMIÈREMENT : Je vais SUPPRIMER tous les devoirs ! Les enfants ne méritent pas cette OPPRESSION !

DEUXIÈMEMENT : GRATUIT pour tout le monde ! Les bonbons, les jeux vidéo, les vélos... GRATUIT !

TROISIÈMEMENT : Plus JAMAIS de légumes dans les cantines ! C'est de la DICTATURE nutritionnelle !

*tape du pied*

"Et QUATRIÈMEMENT : Je vais faire en sorte que tous les adultes ÉCOUTENT les enfants... en les ÉLIMINANT !

*sourit de manière inquiétante*

"C'est du BON SENS, non ?"`,
    hint: 'Ces promesses sont complètement délirantes...',
    type: 'multiple_choice',
    choices: [
      { id: 'a', text: 'C\'est n\'importe quoi, ça ne marche pas comme ça !', isCorrect: true },
      { id: 'b', text: 'Tu es complètement fou !', isCorrect: true },
    ],
    correctResponse: '',
    incorrectResponse: '',
    canRetry: false,
    onCorrect: [
      { type: 'solveRiddle', params: { riddleId: 'demagordon_promises' } },
    ],
  },

  // Troisième phase - Accusations
  demagordon_accusations: {
    id: 'demagordon_accusations',
    npcId: 'obj_demagordon',
    npcName: 'Le Demagordon',
    question: `*Le Demagordon pointe une griffe vers toi*

"TU NE COMPRENDS PAS parce que tu as été MANIPULÉ(E) !

*prend un air faussement triste*

"Les ÉDUCATEURS t'ont lavé le cerveau ! Ils te font croire que la COOPÉRATION c'est bien ! Que l'EMPATHIE c'est important !

*ricane*

"Mais moi je dis la VÉRITÉ : Chacun pour soi ! Les plus forts DOMINENT ! C'est la LOI DE LA NATURE !

*rugit soudainement*

"Et si tu n'es pas d'accord avec moi, c'est que tu es un MOUTON ! Un SUIVEUR du système !

*se calme et sourit*

"Mais je suis GÉNÉREUX. Je te laisse une DERNIÈRE CHANCE de me rejoindre..."`,
    hint: 'Ce discours sent la manipulation à plein nez...',
    type: 'multiple_choice',
    choices: [
      { id: 'a', text: 'Non merci, je préfère l\'empathie et la coopération !', isCorrect: true },
      { id: 'b', text: 'Je refuse d\'écouter tes bêtises plus longtemps !', isCorrect: true },
    ],
    correctResponse: '',
    incorrectResponse: '',
    canRetry: false,
    onCorrect: [
      { type: 'solveRiddle', params: { riddleId: 'demagordon_accusations' } },
    ],
  },

  // Quatrième phase - Le défi du Boggle
  demagordon_challenge: {
    id: 'demagordon_challenge',
    npcId: 'obj_demagordon',
    npcName: 'Le Demagordon',
    question: `*Le Demagordon EXPLOSE de rage*

"ASSEZ ! Tu veux me défier ?! MOI ?!

*ses yeux brillent d'une lueur rouge*

"Très bien ! On va régler ça à MA façon ! Un DUEL... de MOTS !

*fait apparaître une grille de lettres flottante*

"Dans l'Upside Down, les MOTS ont du POUVOIR ! Si tu arrives à trouver assez de mots positifs, tu pourras peut-être... me vaincre.

*ricane*

"Mais personne n'a jamais réussi ! Les mots d'AMOUR, d'AMITIÉ, d'ESPOIR... ça n'existe PAS ici !

*te défie du regard*

"Tu as 90 secondes. 50 points pour me battre. BONNE CHANCE, petit éducateur !"

📝 DÉFI : Trouve des mots dans la grille pour atteindre 50 points !`,
    hint: 'Utilise des mots positifs pour le vaincre !',
    type: 'boggle_puzzle',
    bogglePuzzleConfig: {
      targetScore: 15,
      timeLimit: 90,
      victoryMessage: 'Les mots positifs ont eu raison de la démagogie !',
    },
    correctResponse: '',
    incorrectResponse: '',
    canRetry: true,
  },

  // Victoire contre le Demagordon
  demagordon_defeated: {
    id: 'demagordon_defeated',
    npcId: 'obj_demagordon',
    npcName: 'Le Demagordon',
    question: `*Le Demagordon HURLE de douleur alors que les mots brillent autour de lui*

"NON ! IMPOSSIBLE ! Comment... comment les mots peuvent-ils être si... PUISSANTS ?!

*se recroqueville*

"L'amitié... l'espoir... l'aide... Ces mots me BRÛLENT !

*Cotelette s'avance, les mains levées, une lumière rose émanant de ses paumes*

Cotelette : "C'est fini pour toi, Demagordon. Les belles paroles vides ne peuvent rien contre les VRAIS mots. Ceux qui ont du SENS."

*Le Demagordon commence à se désintégrer*

"Je... je reviendrai ! Il y aura TOUJOURS des gens qui... qui..."

*EXPLOSION DE LUMIÈRE*

Le Demagordon disparaît dans un flash aveuglant !

Cotelette : "Bien joué. Tu as prouvé que les mots ont plus de pouvoir que la démagogie."`,
    hint: '',
    type: 'multiple_choice',
    choices: [
      { id: 'a', text: 'Merci Cotelette ! On fait une bonne équipe !', isCorrect: true },
    ],
    correctResponse: `*Cotelette sourit*

"Les vrais éducateurs savent que les mots construisent. Les démagogues, eux, ne font que détruire.

*te regarde avec fierté*

"Tu as appris quelque chose d'important aujourd'hui : face à la manipulation, la meilleure arme, c'est l'ÉDUCATION."

🎉 Tu as vaincu le Demagordon avec le pouvoir des mots !

+100 XP
Nouveau titre débloqué : "Défenseur des Mots"`,
    incorrectResponse: '',
    canRetry: false,
    onCorrect: [
      { type: 'solveRiddle', params: { riddleId: 'demagordon_defeated' } },
      {
        type: 'addToInventory',
        params: {
          itemId: 'griffe_demagordon',
          itemName: 'Griffe du Demagordon',
          itemDescription: 'Une griffe cristallisée. Rappelle que les mots peuvent vaincre la manipulation.',
          itemIcon: '🦷',
        },
      },
    ],
  },
};

/**
 * Obtient le dialogue approprié pour Dustin
 * Permet aussi de déposer un vélo si le joueur est sur un vélo
 */
export function getDustinDialogue(solvedRiddles: Set<string>, inventory: { id: string }[], onBike?: boolean, currentBikeColor?: 'blue' | 'yellow' | 'red' | null): Riddle {
  // Si la quête vélos est terminée
  if (solvedRiddles.has('dustin_bike_quest_done')) {
    return hawkinsDynamicDialogues.dustin_quest_complete;
  }

  // Si première interaction déjà faite et joueur sur un vélo -> proposer de déposer
  if (solvedRiddles.has('dustin_first_contact') && onBike && currentBikeColor) {
    const blueDeposited = solvedRiddles.has('bike_blue_deposited');
    const yellowDeposited = solvedRiddles.has('bike_yellow_deposited');

    const colorName = currentBikeColor === 'blue' ? 'bleu' : currentBikeColor === 'yellow' ? 'jaune' : 'rouge';
    const ownerName = currentBikeColor === 'blue' ? 'Lucas' : currentBikeColor === 'yellow' ? 'Mike' : 'quelqu\'un';
    const bikeItemId = `bike_${currentBikeColor}`;
    const depositRiddleId = `bike_${currentBikeColor}_deposited`;

    // Vérifier si c'est le dernier vélo à déposer
    const isLastBike = (currentBikeColor === 'blue' && yellowDeposited) ||
                       (currentBikeColor === 'yellow' && blueDeposited);

    // Actions à exécuter
    const onCorrectActions: Array<{ type: string; params: Record<string, unknown> }> = [
      { type: 'dismountBike', params: {} },
      { type: 'removeFromInventory', params: { itemId: bikeItemId } },
      { type: 'solveRiddle', params: { riddleId: depositRiddleId } },
    ];

    // Si c'est le dernier vélo, ajouter la complétion de la quête
    if (isLastBike) {
      onCorrectActions.push({ type: 'solveRiddle', params: { riddleId: 'dustin_bike_quest_done' } });
    }

    return {
      id: `dustin_deposit_${currentBikeColor}`,
      npcId: 'obj_dustin',
      npcName: 'Dustin',
      question: `*Tu arrives avec le vélo ${colorName} de ${ownerName}*

"Oh super, tu as trouvé le vélo de ${ownerName} ! Tu veux que je le range dans le garage ?"`,
      hint: 'Dépose le vélo pour aider Dustin.',
      type: 'multiple_choice',
      choices: [
        { id: 'a', text: 'Oui, range-le !', isCorrect: true },
        { id: 'b', text: 'Non, je le garde', isCorrect: false },
      ],
      correctResponse: isLastBike
        ? `*Dustin prend le vélo et le range dans le garage*

"🚲🚲 Génial, les deux vélos sont maintenant rangés !

Merci beaucoup ! Maintenant va voir ERICA, elle t'attend !"`
        : `*Dustin prend le vélo et le range dans le garage*

"🚲 Parfait, je le mets au garage !

Il reste encore un vélo à trouver... Continue à chercher !"`,
      incorrectResponse: `*Dustin hausse les épaules*

"D'accord, garde-le pour l'instant. Mais ramène-le moi quand tu veux !"`,
      canRetry: false,
      onCorrect: onCorrectActions,
    };
  }

  // Si première interaction déjà faite (sans vélo) - dialogue dynamique selon les vélos déposés
  if (solvedRiddles.has('dustin_first_contact')) {
    const blueDeposited = solvedRiddles.has('bike_blue_deposited');
    const yellowDeposited = solvedRiddles.has('bike_yellow_deposited');

    // Si un vélo est déjà déposé, indiquer lequel reste
    if (blueDeposited && !yellowDeposited) {
      return {
        id: 'dustin_collecting_yellow',
        npcId: 'obj_dustin',
        npcName: 'Dustin',
        question: `*Dustin sourit*

"Super, tu as déjà ramené le vélo bleu de Lucas ! 🚲✓

Il reste le vélo JAUNE de Mike. Il est dans les buissons près de la maison de Vékeuna.

Ramène-le moi et on pourra passer à la suite !"`,
        hint: '',
        type: 'multiple_choice' as const,
        choices: [
          { id: 'a', text: 'Je vais le chercher !', isCorrect: true },
        ],
        correctResponse: '',
        incorrectResponse: '',
        canRetry: false,
      };
    }

    if (yellowDeposited && !blueDeposited) {
      return {
        id: 'dustin_collecting_blue',
        npcId: 'obj_dustin',
        npcName: 'Dustin',
        question: `*Dustin sourit*

"Super, tu as déjà ramené le vélo jaune de Mike ! 🚲✓

Il reste le vélo BLEU de Lucas. Il est près de l'école.

Ramène-le moi et on pourra passer à la suite !"`,
        hint: '',
        type: 'multiple_choice' as const,
        choices: [
          { id: 'a', text: 'Je vais le chercher !', isCorrect: true },
        ],
        correctResponse: '',
        incorrectResponse: '',
        canRetry: false,
      };
    }

    // Aucun vélo déposé encore
    return hawkinsDynamicDialogues.dustin_collecting;
  }

  return hawkinsDynamicDialogues.dustin_first_contact;
}

/**
 * Obtient le dialogue approprié pour Vékeuna
 */
export function getVekeunaDialogue(solvedRiddles: Set<string>): Riddle {
  if (solvedRiddles.has('vekeuna_helped')) {
    return hawkinsDynamicDialogues.vekeuna_resolved;
  }

  return hawkinsDynamicDialogues.vekeuna_first_contact;
}

/**
 * Obtient le dialogue approprié pour le râtelier à vélos
 * Vérifie si le joueur est sur un vélo et propose de le déposer
 */
export function getBikeRackDialogue(onBike: boolean, solvedRiddles: Set<string>, inventory: { id: string }[], currentBikeColor?: 'blue' | 'yellow' | 'red' | null): Riddle {
  // Si Dustin n'a pas encore été rencontré, on ne peut pas commencer la quête vélos
  if (!solvedRiddles.has('dustin_first_contact')) {
    return hawkinsDynamicDialogues.bike_rack_not_ready;
  }

  // Si la quête vélos est déjà terminée
  if (solvedRiddles.has('dustin_bike_quest_done')) {
    return hawkinsDynamicDialogues.bike_rack_complete;
  }

  // Vérifier combien de vélos ont déjà été déposés
  const blueDeposited = solvedRiddles.has('bike_blue_deposited');
  const yellowDeposited = solvedRiddles.has('bike_yellow_deposited');

  // Si les deux sont déposés, marquer la quête comme terminée
  if (blueDeposited && yellowDeposited) {
    return hawkinsDynamicDialogues.bike_rack_complete;
  }

  // Si le joueur est sur un vélo, proposer de le déposer
  if (onBike && currentBikeColor) {
    const colorName = currentBikeColor === 'blue' ? 'bleu' : currentBikeColor === 'yellow' ? 'jaune' : 'rouge';
    const bikeItemId = `bike_${currentBikeColor}`;
    const depositRiddleId = `bike_${currentBikeColor}_deposited`;

    // Vérifier si c'est le dernier vélo à déposer
    const isLastBike = (currentBikeColor === 'blue' && yellowDeposited) ||
                       (currentBikeColor === 'yellow' && blueDeposited);

    // Actions à exécuter
    const onCorrectActions: Array<{ type: string; params: Record<string, unknown> }> = [
      { type: 'dismountBike', params: {} },
      { type: 'removeFromInventory', params: { itemId: bikeItemId } },
      { type: 'solveRiddle', params: { riddleId: depositRiddleId } },
    ];

    // Si c'est le dernier vélo, ajouter la complétion de la quête
    if (isLastBike) {
      onCorrectActions.push({ type: 'solveRiddle', params: { riddleId: 'dustin_bike_quest_done' } });
    }

    return {
      id: `bike_rack_deposit_${currentBikeColor}`,
      npcId: 'obj_bike_rack',
      npcName: 'Râtelier à Vélos',
      question: `*Tu arrives au râtelier avec le vélo ${colorName}*

Veux-tu déposer le vélo ici ?`,
      hint: 'Dépose le vélo pour libérer tes mains.',
      type: 'multiple_choice',
      choices: [
        { id: 'a', text: 'Oui, déposer le vélo', isCorrect: true },
        { id: 'b', text: 'Non, je garde le vélo', isCorrect: false },
      ],
      correctResponse: isLastBike
        ? `*Tu descends du vélo et l'accroches au râtelier*

🚲🚲 Les deux vélos sont maintenant au râtelier !

*Dustin accourt tout sourire*

"Génial ! Tu as retrouvé les vélos ! Merci beaucoup !

*regarde vers une fille près de l'école*

"Maintenant, va parler à ERICA. C'est la petite sœur de Lucas. Elle connaît bien Vékeuna et elle peut t'aider à comprendre ce qui s'est passé.

*pointe vers la faille*

"Et regarde... la FAILLE s'est ouverte ! Quand tu seras prêt(e), tu pourras y entrer pour retrouver Vékeuna."

📍 Parle à ERICA puis entre dans la FAILLE !`
        : `*Tu descends du vélo et l'accroches au râtelier*

🚲 Vélo ${colorName} déposé !

Il reste encore un vélo à trouver...`,
      incorrectResponse: `*Tu décides de garder le vélo pour l'instant*

D'accord, continue à explorer !`,
      canRetry: false,
      onCorrect: onCorrectActions,
    };
  }

  // Vérifier si un seul vélo est déposé
  if (blueDeposited || yellowDeposited) {
    const missingColor = blueDeposited ? 'jaune (Mike)' : 'bleu (Lucas)';
    return {
      id: 'bike_rack_partial',
      npcId: 'obj_bike_rack',
      npcName: 'Râtelier à Vélos',
      question: `*Tu examines le râtelier à vélos*

Il y a déjà un vélo accroché. Il manque encore le vélo ${missingColor}.

Continue à chercher !`,
      hint: 'Trouve le dernier vélo.',
      type: 'multiple_choice',
      choices: [
        { id: 'a', text: 'D\'accord !', isCorrect: true },
      ],
      correctResponse: '',
      incorrectResponse: '',
      canRetry: false,
    };
  }

  // Sinon, dire qu'il faut trouver les vélos
  return hawkinsDynamicDialogues.bike_rack_empty;
}

/**
 * Obtient le dialogue approprié pour la Maison de Dustin (dépôt des vélos)
 * Si le joueur est sur un vélo, propose de le déposer
 */
export function getDustinHouseDialogue(onBike: boolean, solvedRiddles: Set<string>, currentBikeColor?: 'blue' | 'yellow' | 'red' | null): Riddle {
  // Si Dustin n'a pas encore été rencontré
  if (!solvedRiddles.has('dustin_first_contact')) {
    return {
      id: 'dustin_house_not_ready',
      npcId: 'obj_dustin_house',
      npcName: 'Maison de Dustin',
      question: `*Tu regardes la maison de Dustin*

C'est une jolie maison de banlieue typique des années 80.

Va d'abord parler à Dustin, il est devant sa maison !`,
      hint: '',
      type: 'multiple_choice',
      choices: [
        { id: 'a', text: 'D\'accord', isCorrect: true },
      ],
      correctResponse: '',
      incorrectResponse: '',
      canRetry: false,
    };
  }

  // Si la quête vélos est déjà terminée
  if (solvedRiddles.has('dustin_bike_quest_done')) {
    return {
      id: 'dustin_house_complete',
      npcId: 'obj_dustin_house',
      npcName: 'Maison de Dustin',
      question: `*Tu regardes la maison de Dustin*

Les deux vélos sont rangés dans le garage. La quête est terminée !

Va parler à Erica maintenant.`,
      hint: '',
      type: 'multiple_choice',
      choices: [
        { id: 'a', text: 'D\'accord', isCorrect: true },
      ],
      correctResponse: '',
      incorrectResponse: '',
      canRetry: false,
    };
  }

  // Vérifier combien de vélos ont déjà été déposés
  const blueDeposited = solvedRiddles.has('bike_blue_deposited');
  const yellowDeposited = solvedRiddles.has('bike_yellow_deposited');

  // Si le joueur est sur un vélo, proposer de le déposer
  if (onBike && currentBikeColor) {
    const colorName = currentBikeColor === 'blue' ? 'bleu' : currentBikeColor === 'yellow' ? 'jaune' : 'rouge';
    const ownerName = currentBikeColor === 'blue' ? 'Lucas' : currentBikeColor === 'yellow' ? 'Mike' : 'quelqu\'un';
    const bikeItemId = `bike_${currentBikeColor}`;
    const depositRiddleId = `bike_${currentBikeColor}_deposited`;

    // Vérifier si c'est le dernier vélo à déposer
    const isLastBike = (currentBikeColor === 'blue' && yellowDeposited) ||
                       (currentBikeColor === 'yellow' && blueDeposited);

    // Actions à exécuter
    const onCorrectActions: Array<{ type: string; params: Record<string, unknown> }> = [
      { type: 'dismountBike', params: {} },
      { type: 'removeFromInventory', params: { itemId: bikeItemId } },
      { type: 'solveRiddle', params: { riddleId: depositRiddleId } },
    ];

    // Si c'est le dernier vélo, ajouter la complétion de la quête
    if (isLastBike) {
      onCorrectActions.push({ type: 'solveRiddle', params: { riddleId: 'dustin_bike_quest_done' } });
    }

    return {
      id: `dustin_house_deposit_${currentBikeColor}`,
      npcId: 'obj_dustin_house',
      npcName: 'Maison de Dustin',
      question: `*Tu arrives devant la maison avec le vélo ${colorName} de ${ownerName}*

Veux-tu ranger le vélo dans le garage ?`,
      hint: 'Range le vélo pour aider Dustin.',
      type: 'multiple_choice',
      choices: [
        { id: 'a', text: 'Oui, ranger le vélo', isCorrect: true },
        { id: 'b', text: 'Non, je garde le vélo', isCorrect: false },
      ],
      correctResponse: isLastBike
        ? `*Tu ranges le vélo ${colorName} dans le garage*

🚲🚲 Les deux vélos sont maintenant au garage !

📍 Va parler à DUSTIN pour lui dire que c'est fait !`
        : `*Tu ranges le vélo ${colorName} dans le garage*

🚲 Vélo ${colorName} rangé !

Il reste encore un vélo à trouver et à ramener ici...`,
      incorrectResponse: `*Tu décides de garder le vélo pour l'instant*

D'accord, continue à explorer !`,
      canRetry: false,
      onCorrect: onCorrectActions,
    };
  }

  // Si un seul vélo est déposé
  if (blueDeposited || yellowDeposited) {
    const missingColor = blueDeposited ? 'jaune (Mike)' : 'bleu (Lucas)';
    return {
      id: 'dustin_house_partial',
      npcId: 'obj_dustin_house',
      npcName: 'Maison de Dustin',
      question: `*Tu regardes le garage de Dustin*

Il y a déjà un vélo rangé. Il manque encore le vélo ${missingColor}.

Trouve-le et ramène-le ici !`,
      hint: 'Trouve le dernier vélo.',
      type: 'multiple_choice',
      choices: [
        { id: 'a', text: 'D\'accord !', isCorrect: true },
      ],
      correctResponse: '',
      incorrectResponse: '',
      canRetry: false,
    };
  }

  // Le joueur n'est pas sur un vélo et aucun vélo n'est déposé
  return {
    id: 'dustin_house_empty',
    npcId: 'obj_dustin_house',
    npcName: 'Maison de Dustin',
    question: `*Tu regardes la maison de Dustin*

C'est ici qu'il faut ramener les vélos perdus !

- Le vélo BLEU de Lucas est près de l'école
- Le vélo JAUNE de Mike est dans les buissons

Monte sur un vélo et ramène-le ici.`,
    hint: 'Trouve les vélos et ramène-les ici.',
    type: 'multiple_choice',
    choices: [
      { id: 'a', text: 'Compris !', isCorrect: true },
    ],
    correctResponse: '',
    incorrectResponse: '',
    canRetry: false,
  };
}

/**
 * Obtient le dialogue approprié pour Erica
 * Si Dustin n'a pas encore été aidé (quête vélos pas finie), elle renvoie vers Dustin
 */
export function getEricaDialogue(solvedRiddles: Set<string>): Riddle {
  // Si Dustin n'a même pas été rencontré, Erica renvoie vers lui
  if (!solvedRiddles.has('dustin_first_contact')) {
    return hawkinsDynamicDialogues.erica_not_ready;
  }

  // Si la quête de Dustin n'est pas terminée (vélos pas récupérés), message différent
  if (!solvedRiddles.has('dustin_bike_quest_done')) {
    return hawkinsDynamicDialogues.erica_bikes_not_done;
  }

  // Si le puzzle d'Erica est déjà résolu, dialogue de rappel
  if (solvedRiddles.has('erica_puzzle_done')) {
    return hawkinsDynamicDialogues.erica_after_puzzle;
  }

  // Si le walkie-talkie a été complété, montrer le dialogue de succès
  if (solvedRiddles.has('erica_walkie_done')) {
    return hawkinsDynamicDialogues.erica_walkie_success;
  }

  // Si la première interaction est faite, passer au puzzle walkie-talkie
  if (solvedRiddles.has('erica_first_contact')) {
    return hawkinsDynamicDialogues.erica_friendship_puzzle;
  }

  // Première rencontre avec Erica
  return hawkinsDynamicDialogues.erica_first_contact;
}

/**
 * Obtient le dialogue approprié pour le Demagordon
 * Progression: intro -> promises -> accusations -> challenge (boggle) -> defeated
 */
export function getDemagordonDialogue(solvedRiddles: Set<string>): Riddle {
  // Si le Demagordon est déjà vaincu
  if (solvedRiddles.has('demagordon_defeated')) {
    // Le Demagordon ne devrait plus apparaître (hideAfterRiddleSolved)
    return hawkinsDynamicDialogues.demagordon_defeated;
  }

  // Si le défi du Boggle est passé avec succès
  if (solvedRiddles.has('demagordon_challenge_won')) {
    return hawkinsDynamicDialogues.demagordon_defeated;
  }

  // Si les accusations sont passées, lancer le défi
  if (solvedRiddles.has('demagordon_accusations')) {
    return hawkinsDynamicDialogues.demagordon_challenge;
  }

  // Si les promesses sont passées, passer aux accusations
  if (solvedRiddles.has('demagordon_promises')) {
    return hawkinsDynamicDialogues.demagordon_accusations;
  }

  // Si l'intro est passée, passer aux promesses
  if (solvedRiddles.has('demagordon_intro')) {
    return hawkinsDynamicDialogues.demagordon_promises;
  }

  // Première rencontre avec le Demagordon
  return hawkinsDynamicDialogues.demagordon_intro;
}

/**
 * Obtient le dialogue de la faille dimensionnelle (monde réel -> Upside Down)
 */
export function getRiftDialogue(): Riddle {
  return hawkinsDynamicDialogues.rift_enter;
}

/**
 * Obtient le dialogue de la faille de retour (Upside Down -> monde réel)
 */
export function getRiftReturnDialogue(): Riddle {
  return hawkinsDynamicDialogues.rift_return;
}
